/*
 * Loxa × DreamCloud
 *
 * Adds Loxa product protection to DreamCloud's product and cart pages.
 * Insurance is a separate cart line whose quantity follows its product, and is
 * removed when the product is removed.
 *
 * Load once in scripts.liquid, after the Loxa embed:
 *   <script src="{{ 'loxa-dreamcloud.js' | asset_url }}" defer="defer"></script>
 */
(function () {
  "use strict";

  // ═══════════════════════════════════════════════════════════════════════════
  // Shared constants + helpers
  // ═══════════════════════════════════════════════════════════════════════════
  var LOG_PREFIX = "[Loxa DreamCloud]";
  var ANCHOR_ID = "loxa-shopify-addon-anchor";
  var OFFER_ID = "loxa-shopify-offer";
  var CHECKBOX_ID = "loxa-insurance-checkbox";
  var TITLE_CLASS = "loxa-add-on-title";
  var DEFAULT_TERM = "5";
  var SEPARATE_ADD_HEADER = "X-Loxa-Separate-Add"; // our own cart writes
  var RECONCILE_HEADER = "X-Loxa-Dreamcloud-Reconcile"; // load-time sweep
  var ATOMIC_HEADER = "X-Loxa-Dreamcloud-Atomic"; // qty-sync override
  var ATC_DISABLED_ATTR = "data-loxa-dc-disabled";

  function log(msg, obj) {
    try {
      if (window.LoxaShopify && typeof window.LoxaShopify.log === "function") {
        window.LoxaShopify.log(LOG_PREFIX + " " + msg, obj);
      }
    } catch (e) {}
  }

  // Route handled exceptions to Bugsnag. Core error()/log() never reach
  // Bugsnag and suppress on debugMode, so we try a notify hook first, then
  // fall back to console.error — never a silent catch.
  function notify(msg, err, meta) {
    var detail = Object.assign({ integration: "dreamcloud" }, meta || {});
    try {
      if (
        window.LoxaShopify &&
        typeof window.LoxaShopify.notify === "function"
      ) {
        window.LoxaShopify.notify(LOG_PREFIX + " " + msg, err, detail);
        return;
      }
      if (window.Bugsnag && typeof window.Bugsnag.notify === "function") {
        window.Bugsnag.notify(err || new Error(msg), function (event) {
          try {
            event.context = LOG_PREFIX + " " + msg;
            event.addMetadata("loxa", detail);
          } catch (e) {}
        });
        return;
      }
      if (
        window.LoxaShopify &&
        typeof window.LoxaShopify.error === "function"
      ) {
        window.LoxaShopify.error(LOG_PREFIX + " " + msg, err || detail);
      }
    } catch (e) {}
    console.error(LOG_PREFIX + " " + msg, err || "", detail);
  }

  function shopRoot() {
    try {
      var r =
        (window.Shopify &&
          window.Shopify.routes &&
          window.Shopify.routes.root) ||
        "/";
      return r.replace(/\/$/, "");
    } catch (e) {
      return "";
    }
  }

  // Deterministic pairing key. Owned by us; carried by core. Stable across
  // cart re-renders / reorders so qty-sync + orphan-sweep can find pairs.
  function linkIdFor(productVariantId, planCode) {
    return "lx_" + String(productVariantId) + "_" + String(planCode || "");
  }

  // All Loxa × DreamCloud styling, injected by the integration so it's always
  // loaded (no reliance on the block's Custom CSS field). Runs on PDP + /cart.
  // Styles: the offer box (Nectar-style, theme grey), the checkbox-simple card
  // row, the See Details link, every CTA → DreamCloud gold (.bttn--primary),
  // and a cart-line un-hide. Palette: gold #ddb15e / navy #1b222e; box bg uses
  // the theme's own --colors-grey100 token.
  function injectStyles() {
    if (document.getElementById("loxa-dreamcloud-styles")) return;
    var GOLD = "#ddb15e";
    var NAVY = "#1b222e";
    var css = [
      // Offer box (hidden by default; ensureBoxLayout reveals it when insurable).
      // Base framing is shared; per-template background/borders are set below so
      // each page (mattress/bedframe/bundle) frames the box to suit its layout.
      "#" +
        ANCHOR_ID +
        " { display: none; margin: 0 !important; padding: 16px 20px !important; font-family: inherit !important; color: " +
        NAVY +
        " !important; }",
      // Bedframe page: theme grey panel + matching side borders (uses the
      // theme's own tokens so it tracks DreamCloud's palette).
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="bedframe"] { background: var(--colors-grey50) !important; border-left: 1px solid var(--colors-grey100) !important; border-right: 1px solid var(--colors-grey100) !important; }',
      // Bedframe mobile: the box's container is inset from the theme's Size/ATC
      // column, so pull it out with a -24px breakout below the 990px breakpoint.
      // Desktop needs no adjustment — the box already aligns there.
      "@media (max-width: 989px) { #" +
        ANCHOR_ID +
        '[data-loxa-template="bedframe"] { margin-left: -24px !important; margin-right: -24px !important; } }',
      // Mattress: warm sand panel to match the page (no side borders).
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] { background: #f8f7f5 !important; border-radius: 4px !important; }',
      // Bundle: keep the soft off-white panel (no side borders).
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="bundle"] { background: #fafaf8 !important; }',
      // Mattress only: bottom gap so the box doesn't touch the ATC.
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] { margin-bottom: 16px !important; }',
      // Header: match the theme's accordion labels (Size/Colour) — bold
      // sans-serif, NOT the serif product__title. font:inherit picks up GT
      // America; we set weight + size to read as a label, not a section title.
      "#" +
        ANCHOR_ID +
        " .loxa-protect-heading { margin: 0 0 12px !important; padding: 0 !important; font: inherit !important; font-weight: 700 !important; font-size: 12px !important; line-height: 1.2 !important; color: " +
        NAVY +
        " !important; }",
      // Mattress only: client-requested heading treatment (lighter weight,
      // larger size, softer colour) — bedframe keeps the shared label style
      // above since its box sits inside the theme's own accordion block.
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] .loxa-protect-heading { font-weight: 500 !important; font-size: 16px !important; color: #383838 !important; }',
      // Bedframe only: client-requested lighter weight, 14px, softer colour.
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="bedframe"] .loxa-protect-heading { font-weight: 500 !important; font-size: 14px !important; color: #383838 !important; }',
      // Card row (block's checkbox-simple), transparent inside the box.
      "#" +
        OFFER_ID +
        " { margin: 0 !important; padding: 0 !important; background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; font-family: inherit !important; color: " +
        NAVY +
        " !important; }",
      // Lock a stable row height so the card area doesn't resize when the block
      // swaps content on a variant change (it height-locks then rebuilds a fresh
      // #loxa-shopify-offer, which would otherwise jump bigger→smaller). A fixed
      // min-height keeps the spinner and the rebuilt row the same size.
      "#" +
        OFFER_ID +
        " .loxa-add-on-inner { display: flex !important; align-items: center !important; justify-content: space-between !important; gap: 16px !important; padding: 0 !important; min-height: 28px !important; }",
      "#" +
        OFFER_ID +
        " .loxa-simple-left { display: flex !important; align-items: center !important; gap: 12px !important; min-width: 0 !important; }",
      "#" +
        OFFER_ID +
        " .loxa-offer-checkbox { flex: 0 0 auto !important; width: 20px !important; height: 20px !important; margin: 0 !important; cursor: pointer !important; accent-color: " +
        NAVY +
        " !important; }",
      // Hide the Loxa logo the block renders in its card — DreamCloud doesn't want it.
      "#" + OFFER_ID + " .loxa-add-on-logo { display: none !important; }",
      "#" +
        OFFER_ID +
        " ." +
        TITLE_CLASS +
        " { flex: 1 1 auto !important; min-width: 0 !important; background: none !important; font: inherit !important; font-size: 12px !important; color: " +
        NAVY +
        " !important; }",
      // Mattress only: the product column is narrower than other templates, so
      // the title "Add 5 Year Protection - £XX.XX" wrapped to two lines. Keep it
      // on one line (no wrap) and let it size to its content; the row stays
      // space-between so See Details still sits pinned at the far edge. Also
      // client-requested 14px (bedframe keeps the shared 12px above, since its
      // box sits inside the theme's own accordion block).
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] #' +
        OFFER_ID +
        " ." +
        TITLE_CLASS +
        " { flex: 0 1 auto !important; white-space: nowrap !important; font-size: 14px !important; }",
      "#" +
        OFFER_ID +
        " .loxa-details-link, #" +
        OFFER_ID +
        " .loxa-learn-more-link { flex: 0 0 auto !important; margin: 0 !important; padding: 0 !important; border: 0 !important; background: none !important; box-shadow: none !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; font-size: 12px !important; text-decoration: underline !important; white-space: nowrap !important; cursor: pointer !important; }",
      // Mattress only: client-requested 14px for "See Details" (bedframe keeps
      // the shared 12px above).
      "#" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] #' +
        OFFER_ID +
        " .loxa-details-link, #" +
        ANCHOR_ID +
        '[data-loxa-template="mattress"] #' +
        OFFER_ID +
        " .loxa-learn-more-link { font-size: 14px !important; }",
      "#" +
        OFFER_ID +
        " .loxa-details-link:hover, #" +
        OFFER_ID +
        " .loxa-learn-more-link:hover { opacity: 0.8 !important; }",
      "@media (max-width: 480px) { #" +
        OFFER_ID +
        " .loxa-add-on-inner { flex-wrap: wrap !important; row-gap: 8px !important; } }",
      // During a variant change the block injects a centred spinner into the
      // card and inline-locks the card height. Pin the spinner to our row height
      // (and keep the box from inflating) so the swap doesn't scale up/down.
      "#" +
        OFFER_ID +
        " { min-height: 28px !important; height: auto !important; }",
      "#" +
        OFFER_ID +
        " .loxa-spinner-wrap { height: 28px !important; justify-content: flex-start !important; }",
      "#" +
        OFFER_ID +
        " .loxa-spinner { width: 20px !important; height: 20px !important; margin: 0 !important; }",
      // Sidebar. Wider than the block's 400px default; keep the block's 90vw
      // cap (the block closes via right:-100%, width-independent, so widening
      // is safe — the panel still slides fully off-screen when closed).
      "#loxa-shopify-sidebar { font-family: inherit !important; max-width: 510px !important; }",
      "#loxa-shopify-sidebar .loxa-sidebar-option-price { color: " +
        NAVY +
        " !important; }",
      // Mobile: hide the scrollbar gutter (the panel is the scroller —
      // overflow-y:auto stays, so it's still scrollable; we only stop the bar
      // being painted). scrollbar-width:none = Firefox; ::-webkit = iOS/Android.
      "@media (max-width: 768px) { #loxa-shopify-sidebar { scrollbar-width: none !important; -ms-overflow-style: none !important; } #loxa-shopify-sidebar::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; } }",
      // PRIMARY CTA → solid gold ("Add with Protection" / "Add Protection" /
      // Back to product / inclusive re-add). The block scopes its own styling up
      // to (id + attr + class) e.g. `[data-loxa-theme="neutral"] .loxa-sidebar-cta`
      // AND loads its CSS after ours, so an equal-specificity rule loses on
      // source order. We out-specify it with (id + attr + class + class) by
      // adding the .loxa-sidebar-actions parent — beats every block rule outright.
      // NOTE: .loxa-second-chance-continue is the SECONDARY here — styled below.
      "#loxa-shopify-sidebar[data-loxa-theme] .loxa-sidebar-actions .loxa-sidebar-cta, #loxa-shopify-sidebar .loxa-sidebar-actions .loxa-sidebar-cta, #loxa-shopify-sidebar[data-loxa-theme] .loxa-inclusive-readd, #loxa-shopify-sidebar[data-loxa-theme] .loxa-back-to-product { background: " +
        GOLD +
        " !important; background-color: " +
        GOLD +
        " !important; border: 1px solid " +
        GOLD +
        " !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; font-weight: 600 !important; border-radius: 8px !important; text-decoration: none !important; cursor: pointer !important; }",
      "#loxa-shopify-sidebar[data-loxa-theme] .loxa-sidebar-actions .loxa-sidebar-cta:hover, #loxa-shopify-sidebar .loxa-sidebar-actions .loxa-sidebar-cta:hover, #loxa-shopify-sidebar[data-loxa-theme] .loxa-inclusive-readd:hover, #loxa-shopify-sidebar[data-loxa-theme] .loxa-back-to-product:hover { opacity: 0.92 !important; }",
      // SECONDARY CTA → gold border only, transparent fill ("Continue without
      // protection" + "No thanks"). Same specificity trick to beat the block's
      // navy-border default. These are BUTTONS (boxed), not text links.
      "#loxa-shopify-sidebar[data-loxa-theme] .loxa-second-chance-continue, #loxa-shopify-sidebar .loxa-second-chance-continue, #loxa-shopify-sidebar[data-loxa-theme] .loxa-sidebar-actions .loxa-sidebar-no-thanks, #loxa-shopify-sidebar .loxa-sidebar-actions .loxa-sidebar-no-thanks { background: transparent !important; background-color: transparent !important; border: 1px solid " +
        GOLD +
        " !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; font-weight: 600 !important; border-radius: 8px !important; text-decoration: none !important; cursor: pointer !important; }",
      "#loxa-shopify-sidebar[data-loxa-theme] .loxa-second-chance-continue:hover, #loxa-shopify-sidebar .loxa-second-chance-continue:hover, #loxa-shopify-sidebar .loxa-sidebar-actions .loxa-sidebar-no-thanks:hover { background: " +
        GOLD +
        " !important; opacity: 0.92 !important; }",
      // Cart-context CTAs (render outside the sidebar; block styles these with
      // bare-class rules, so a bare-class rule ties on specificity and wins on
      // source order since our <style> appends after the block's).
      ".loxa-add-protection-btn, .loxa-cart-add-protection, .loxa-cart-ins-cta { background: " +
        GOLD +
        " !important; background-color: " +
        GOLD +
        " !important; border: 1px solid " +
        GOLD +
        " !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; font-weight: 600 !important; border-radius: 8px !important; text-decoration: none !important; cursor: pointer !important; }",
      ".loxa-add-protection-btn:hover, .loxa-cart-add-protection:hover, .loxa-cart-ins-cta:hover { opacity: 0.92 !important; }",
      // "Remove Protection" link (shown once insurance is selected) → quiet
      // underlined text. NOT .loxa-sidebar-no-thanks — that's a bordered button
      // (styled above); only .loxa-sidebar-remove reads as a text link.
      "#loxa-shopify-sidebar .loxa-sidebar-remove, #loxa-shopify-sidebar[data-loxa-theme] .loxa-sidebar-remove { background: transparent !important; border: 0 !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; text-decoration: underline !important; cursor: pointer !important; }",
      "#loxa-shopify-sidebar .loxa-sidebar-remove:hover { opacity: 0.8 !important; }",
      ".loxa-cart-banner-remove { background: transparent !important; border: 0 !important; color: " +
        NAVY +
        " !important; font-family: inherit !important; text-decoration: underline !important; cursor: pointer !important; }",
      ".loxa-cart-banner-remove:hover { opacity: 0.8 !important; }",
      // Cart-line un-hide (defence-in-depth; eager hider only arms in separate mode).
      "[data-loxa-hidden-insurance] { display: revert !important; }",
      "cart-item[data-loxa-hidden-insurance], .cart-item[data-loxa-hidden-insurance], .cart__item[data-loxa-hidden-insurance], [data-cart-item][data-loxa-hidden-insurance] { display: revert !important; }",
      // ── Cart "Protect your basket" toggle CTA (/cart page) ──
      ".loxa-dc-cart { display: flex !important; flex-direction: column !important; gap: 10px !important; margin: 16px 0 0 !important; padding: 14px 16px !important; background: #eeeae2 !important; border-radius: 4px !important; font-family: inherit !important; color: " + NAVY + " !important; }",
      ".loxa-dc-cart__heading { margin: 0 !important; padding: 0 !important; font-weight: 500 !important; font-size: 14px !important; line-height: 1.2 !important; color: #383838 !important; }",
      ".loxa-dc-cart__row { display: flex !important; align-items: center !important; justify-content: space-between !important; gap: 16px !important; }",
      ".loxa-dc-cart__toggle { display: flex !important; align-items: center !important; gap: 12px !important; cursor: pointer !important; min-width: 0 !important; margin: 0 !important; }",
      ".loxa-dc-cart__input { width: 0 !important; height: 0 !important; opacity: 0 !important; position: absolute !important; }",
      ".loxa-dc-cart__track { flex: 0 0 auto !important; width: 40px !important; height: 22px !important; border-radius: 11px !important; background: #c9c2b4 !important; position: relative !important; transition: background .15s ease !important; }",
      ".loxa-dc-cart__track::after { content: '' !important; position: absolute !important; top: 2px !important; left: 2px !important; width: 18px !important; height: 18px !important; border-radius: 50% !important; background: #fff !important; transition: transform .15s ease !important; }",
      ".loxa-dc-cart__input:checked + .loxa-dc-cart__track { background: " + GOLD + " !important; }",
      ".loxa-dc-cart__input:checked + .loxa-dc-cart__track::after { transform: translateX(18px) !important; }",
      ".loxa-dc-cart__label { font-size: 14px !important; line-height: 1.3 !important; color: " + NAVY + " !important; }",
      ".loxa-dc-cart__details { flex: 0 0 auto !important; background: none !important; border: 0 !important; padding: 0 !important; color: " + NAVY + " !important; font-family: inherit !important; font-size: 13px !important; text-decoration: underline !important; cursor: pointer !important; white-space: nowrap !important; }",
      '.loxa-dc-cart[data-state="applying"] .loxa-dc-cart__toggle, .loxa-dc-cart[data-state="removing"] .loxa-dc-cart__toggle, .loxa-dc-cart[data-state="quoting"] .loxa-dc-cart__toggle { opacity: .6 !important; pointer-events: none !important; }',
      ".loxa-dc-cart__error { color: #b00020 !important; font-size: 13px !important; }",
    ].join("\n");
    var style = document.createElement("style");
    style.id = "loxa-dreamcloud-styles";
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  // We depend on the headless data API (LoxaShopifyDev), exactly like
  // loxa-nectar.js / loxa-bundled.js. The app block (LoxaShopify) runs with
  // cart_mode = bundled (renders the offer card into our anchor; the eager
  // insurance-line hider stays disarmed). We inject the anchor + styling and
  // switch the cart behaviour to a separate visible insurance line.
  function devReady() {
    return (
      typeof window.LoxaShopifyDev !== "undefined" &&
      typeof window.LoxaShopifyDev.fetchInsurance === "function" &&
      typeof window.LoxaShopifyDev.getInsuranceMap === "function" &&
      typeof window.LoxaShopifyDev.findInsuranceVariantByPrice === "function"
    );
  }

  function waitFor(predicate, cb, onTimeout, attempt) {
    attempt = attempt || 0;
    try {
      if (predicate()) {
        cb();
        return;
      }
    } catch (e) {}
    if (attempt > 80) {
      if (onTimeout) onTimeout();
      return;
    }
    setTimeout(function () {
      waitFor(predicate, cb, onTimeout, attempt + 1);
    }, 100);
  }

  // ─── Quote helpers (shared by PDP + cart) ──────────────────────────────────
  // Normalise the core proxy response into the small shape both layers use.
  // `cleanTitle` is the authoritative product title the caller already resolved
  // (ShopifyAnalytics variant name / cart line product_title). Prefer it over
  // resp.product_title, which the proxy merely echoes back from the request and
  // can be a DOM-scraped string with adjacent badge/price text baked in.
  function normaliseQuote(resp, variantId, pricePounds, cleanTitle) {
    if (!resp || resp.error) return null;
    var arr = Array.isArray(resp.insurances) ? resp.insurances : [];
    return {
      variantId: String(variantId),
      pricePounds: typeof pricePounds === "number" ? pricePounds : 0,
      // Keep the raw proxy response — it's the exact shape the core block's
      // renderInsuranceOffer/openSidebar expect, so we can drive a fresh
      // block render for the current variant (badge + sidebar) without a reload.
      raw: resp,
      insurable: resp.insurable !== false && arr.length > 0,
      category: resp.insurance_category || "Furniture",
      productTitle: cleanTitle || resp.product_title || "",
      insurances: arr.map(function (ins) {
        return {
          code: ins.code || "",
          term: String(ins.insurance_term || ins.term || DEFAULT_TERM),
          insurance_term: String(
            ins.insurance_term || ins.term || DEFAULT_TERM,
          ),
          insurance_price:
            typeof ins.insurance_price === "number"
              ? ins.insurance_price
              : parseFloat(ins.insurance_price || "0"),
          premium_signature: ins.premium_signature || "",
          name: ins.name || "Product Protection",
          insurance_content: ins.insurance_content || null,
        };
      }),
    };
  }

  function premiumOf(plan) {
    if (!plan) return 0;
    return typeof plan.insurance_price === "number"
      ? plan.insurance_price
      : parseFloat(plan.insurance_price || "0") || 0;
  }

  function pickInsuranceForTerm(quote, term) {
    if (!quote || !quote.insurable || !quote.insurances.length) return null;
    var match = quote.insurances.find(function (ins) {
      return String(ins.term) === String(term);
    });
    return match || quote.insurances[0];
  }

  // Build the separate-line insurance properties. Shape is checkout-read —
  // mirrors loxa-nectar.js buildInsuranceProps + our deterministic _loxaLinkId.
  function buildInsuranceProps(
    plan,
    quote,
    insVarNumericId,
    insuredVariantId,
    linkId,
  ) {
    return {
      _loxaInsuranceCodeSep: plan.code,
      _Premium: "£" + premiumOf(plan).toFixed(2),
      _loxaSignature: plan.premium_signature || "",
      _loxaInsuranceProductId: String(insVarNumericId),
      _insuredProductId: String(insuredVariantId),
      "Insured Product": quote.productTitle || "",
      _sumInsured: (quote.pricePounds || 0).toFixed(2),
      _Category: quote.category || "Furniture",
      _loxaTerm: String(plan.insurance_term || plan.term || DEFAULT_TERM),
      Cover:
        String(plan.insurance_term || plan.term || DEFAULT_TERM) + " Year",
      _loxaLinkId: linkId,
    };
  }

  function isInsuranceLine(item) {
    return !!(item && item.properties && item.properties._loxaInsuranceCodeSep);
  }

  // Resolve a premium → numeric insurance variant id via core's data API.
  function resolveInsuranceVariantId(plan) {
    var Dev = window.LoxaShopifyDev;
    return Promise.resolve(Dev.getInsuranceMap())
      .then(function (map) {
        var res = Dev.findInsuranceVariantByPrice(premiumOf(plan), map);
        if (!res || !res.variantId) return null;
        // findInsuranceVariantByPrice returns a GID; strip to numeric.
        return String(res.variantId).replace(
          /^gid:\/\/shopify\/ProductVariant\//,
          "",
        );
      })
      .catch(function (e) {
        notify("resolveInsuranceVariantId failed", e, {
          code: plan && plan.code,
        });
        return null;
      });
  }

  // ─── Raw cart writes (ported from loxa-cart-addon.js) ──────────────────────
  function readCart() {
    return fetch(shopRoot() + "/cart.js", {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function (e) {
        notify("readCart failed", e);
        return null;
      });
  }

  // Add a single cart line (product OR insurance) via /cart/add.js with our
  // own marker header so the qty-sync wrapper ignores our writes.
  function addLineRaw(variantId, quantity, properties) {
    return addLinesRaw([
      {
        id: parseInt(variantId, 10),
        quantity: quantity,
        properties: properties,
      },
    ]);
  }

  // Add MULTIPLE lines in ONE /cart/add.js request. This is the Bespoke
  // Shipping workaround (mirrors loxa-nectar.js): Bespoke wraps fetch and
  // mutates cart lines after every /cart/add. Adding product + insurance as
  // separate sequential requests lets Bespoke run its pass between them and
  // strip our insurance properties. Sending all lines in a single POST means
  // Bespoke runs ONCE on the complete cart and can't interleave/strip.
  function addLinesRaw(items) {
    var headers = { "Content-Type": "application/json" };
    headers[SEPARATE_ADD_HEADER] = "1";
    return fetch(shopRoot() + "/cart/add.js", {
      method: "POST",
      headers: headers,
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.ok ? r.json() : Promise.reject(new Error("add.js " + r.status));
    });
  }

  function updateLineQuantitiesRaw(updates) {
    var headers = { "Content-Type": "application/json" };
    headers[SEPARATE_ADD_HEADER] = "1";
    headers[RECONCILE_HEADER] = "1";
    return fetch(shopRoot() + "/cart/update.js", {
      method: "POST",
      headers: headers,
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      return r.ok
        ? r.json()
        : Promise.reject(new Error("update.js " + r.status));
    });
  }

  // Update one line's properties IN PLACE (qty preserved) via /cart/change.js.
  // Marked with RECONCILE_HEADER so our own atomicChange fetch-wrapper passes
  // it through instead of hijacking it. Caller passes the FULL property set —
  // /cart/change.js replaces properties wholesale, so merge before calling.
  function changeLinePropertiesRaw(lineKey, properties) {
    var headers = { "Content-Type": "application/json" };
    headers[SEPARATE_ADD_HEADER] = "1";
    headers[RECONCILE_HEADER] = "1";
    return fetch(shopRoot() + "/cart/change.js", {
      method: "POST",
      headers: headers,
      body: JSON.stringify({ id: lineKey, properties: properties }),
    }).then(function (r) {
      return r.ok
        ? r.json()
        : Promise.reject(new Error("change.js " + r.status));
    });
  }

  // Chain promise-returning thunks one at a time (Shopify serialises cart
  // writes; parallel → 409). Settles all, never rejects.
  function runSequentially(thunks) {
    var results = [];
    return thunks
      .reduce(function (chain, thunk) {
        return chain.then(function () {
          return Promise.resolve()
            .then(thunk)
            .then(
              function (v) {
                results.push({ ok: true, value: v });
              },
              function (e) {
                results.push({ ok: false, error: e });
              },
            );
        });
      }, Promise.resolve())
      .then(function () {
        return results;
      });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PDP LAYER
  // ═══════════════════════════════════════════════════════════════════════════
  function initPdp() {
    if (window.__loxaDcPdpInstalled) return;
    window.__loxaDcPdpInstalled = true;

    var TEMPLATE = pdpTemplate();
    if (TEMPLATE === "unknown") {
      log("PDP excluded (template=" + TEMPLATE + ")");
      return;
    }
    // bundle behaves exactly like bedframe: anchor only, the block renders the
    // offer + insures the whole bundle on its native /cart/add. Only the anchor
    // position/styling differs per page (see anchorSelector + injectStyles).
    log("PDP init (template=" + TEMPLATE + ")");

    var Dev = window.LoxaShopifyDev;
    var quoteCache = {}; // variantId -> normalised quote
    var lastVariantId = null;
    var lastFrameVariantId = null;
    var lastProductPath = window.location.pathname; // detect Base/product swaps

    // ── Offer UI (self-rendered, mirrors loxa-nectar.js) ───────────────────────
    // We render our own offer card + checkbox and populate the core block's
    // sidebar shell. The core block runs with disable_main_flow ON, so it loads
    // the data API (LoxaShopifyDev) + sidebar shell but renders no competing UI.
    // Styling uses DreamCloud-sympathetic tokens (CSS vars at the top) per the
    // partner-palette rule — tune the hex values to DreamCloud's exact brand.
    // Inject the anchor before the price column (`.price-col`, which holds the
    // "Similar Brands Avg Price" row) so the offer sits ABOVE it. Fall back to
    // the ATC wrapper if the price column isn't found, so the offer always
    // mounts somewhere sensible. Candidate list = resilient to a class change.
    function anchorSelector() {
      // Anchor target differs per template (offer mounts just before it):
      //   mattress → .product-form__atc-wrapper--stacked (no .price-col here)
      //   bedframe → .price-col (above the "Similar Brands Avg Price" row)
      //   bundle   → .atc-wrap (the bundle's ATC container; sits above ATC)
      // Each lists fallbacks so the anchor always mounts somewhere sensible.
      if (TEMPLATE === "mattress")
        return [
          ".product-form__atc-wrapper--stacked",
          ".product-form__atc-wrapper",
        ];
      if (TEMPLATE === "bundle")
        return [".atc-wrap", ".price-col", 'form[action="/cart/add"]'];
      return [".price-col", ".product-form__buttons"]; // bedframe
    }
    function injectAnchor() {
      try {
        if (document.getElementById(ANCHOR_ID)) return true;
        var sels = anchorSelector();
        var target = null;
        for (var i = 0; i < sels.length; i++) {
          target = document.querySelector(sels[i]);
          if (target) break;
        }
        if (!target || !target.parentNode) {
          log("anchor target missing");
          return false;
        }
        var anchor = document.createElement("div");
        anchor.id = ANCHOR_ID;
        anchor.setAttribute("data-loxa-shopify-anchor", "addon");
        // Template hook so the box can be styled per page (mattress/bedframe/
        // bundle sit in different layouts and want different framing).
        anchor.setAttribute("data-loxa-template", TEMPLATE);
        // "Protect your purchase" header above the block's card (Nectar style).
        // A plain element styled in injectStyles() to match the theme's
        // accordion labels ("Size"/"Colour" — bold sans-serif), NOT the serif
        // product__title. The block renders its offer card as the anchor's next
        // sibling; ensureBoxLayout() pulls it inside this anchor → one box.
        var heading = document.createElement("div");
        heading.className = "loxa-protect-heading";
        heading.textContent = "Protect your purchase";
        anchor.appendChild(heading);
        target.parentNode.insertBefore(anchor, target);
        return true;
      } catch (e) {
        notify("injectAnchor failed", e);
        return false;
      }
    }

    // The core block inserts #loxa-shopify-offer as the anchor's NEXT SIBLING.
    // Move it inside the anchor (after our heading) so the bordered box wraps
    // both. Idempotent: once the offer is already a child, this no-ops.
    //
    // Only show the box when there's a real offer (the block renders the card
    // with a priced title for insurable products only). No offer / empty title
    // → hide the whole box, so we never show an empty "Protect your purchase".
    function offerIsLive() {
      var offer = document.getElementById(OFFER_ID);
      if (!offer) return false;
      var titleEl = offer.querySelector("." + TITLE_CLASS);
      var txt = titleEl ? (titleEl.textContent || "").trim() : "";
      // The block fills the title with "Add N Year Protection - £X" once a
      // quote resolves; a £ figure means it's a genuine, priced offer.
      return /£\s*\d/.test(txt);
    }
    var inEnsureBoxLayout = false;
    function ensureBoxLayout() {
      if (inEnsureBoxLayout) return;
      inEnsureBoxLayout = true;
      try {
        var anchor = document.getElementById(ANCHOR_ID);
        if (!anchor) return;
        var offer = document.getElementById(OFFER_ID);
        // Cold-load race fix: the block renders its card ONCE, and bails if our
        // anchor wasn't present yet (loxa-shopify.js renderInsuranceOffer →
        // `if (!addonAnchor) return`). On a cold load our anchor often lands
        // after that single attempt, so the card never appears until a refresh.
        // Re-poke the block's own render into our anchor when EITHER the card is
        // missing OR it mounted but isn't live yet (no priced title). Idempotent:
        // the block removes any existing #loxa-shopify-offer first, so repeated
        // calls are safe and no-op visually once a priced card is in place.
        if (!offer || !offerIsLive()) repokeBlockRender();
        offer = document.getElementById(OFFER_ID);
        if (offer && offer.parentNode !== anchor) {
          anchor.appendChild(offer);
        }
        anchor.style.display = offerIsLive() ? "block" : "none";
      } catch (e) {
        notify("ensureBoxLayout failed", e);
      } finally {
        inEnsureBoxLayout = false;
      }
    }

    // The block re-inserts #loxa-shopify-offer as the anchor's NEXT SIBLING on
    // EVERY render — including its own internal ones we don't trigger, e.g. the
    // sidebar's "Remove Protection" (updatePrimaryCardSelection("__none") →
    // renderInsuranceOffer). Our poll re-parents it into the box, but only on
    // the next 200ms tick, so for up to 200ms the box shows just the orphaned
    // heading — reads as "the whole box vanished". Wrap the block's render once
    // so ensureBoxLayout() runs synchronously after ANY render, no flash.
    var blockRenderWrapped = false;
    function wrapBlockRender() {
      try {
        var block = window.LoxaShopify;
        if (
          blockRenderWrapped ||
          !block ||
          typeof block.renderInsuranceOffer !== "function"
        ) {
          return;
        }
        var original = block.renderInsuranceOffer;
        block.renderInsuranceOffer = function () {
          var result = original.apply(this, arguments);
          // ensureBoxLayout → repokeBlockRender → renderInsuranceOffer would
          // recurse back into this wrapper. Skip the re-parent when we're
          // already inside an ensureBoxLayout pass; that pass re-parents anyway.
          if (!inEnsureBoxLayout) {
            try {
              ensureBoxLayout();
            } catch (e) {
              notify("wrapped render ensureBoxLayout failed", e);
            }
          }
          return result;
        };
        blockRenderWrapped = true;
      } catch (e) {
        notify("wrapBlockRender failed", e);
      }
    }

    // Ask the core block to (re-)render its offer card into our anchor, using
    // the data it already fetched. No-op until the block has booted + quoted.
    function repokeBlockRender() {
      try {
        var block = window.LoxaShopify;
        if (
          block &&
          typeof block.renderInsuranceOffer === "function" &&
          block.lastInsuranceData
        ) {
          block.renderInsuranceOffer(block.lastInsuranceData, {});
        }
      } catch (e) {
        notify("repokeBlockRender failed", e);
      }
    }

    // The core block renders + updates the offer card, including the title and
    // its premium for the mattress variant. The ONE thing the block can't know
    // is DreamCloud's frame-bundler add-on, so on mattress PDPs we add the
    // selected frame's premium on top of the price the block put in the title
    // (mirrors loxa-bundled.js). We never render the card — we read + rewrite.
    function titleEl() {
      return document.querySelector("#" + OFFER_ID + " ." + TITLE_CLASS);
    }

    // The frame premium to add to the block's title for the current term.
    function frameAddOn() {
      return frameAddOnForTerm(DEFAULT_TERM);
    }

    // The selected frame's premium for a SPECIFIC term (3 or 5), so each sidebar
    // option can show the combined mattress+frame price for its own term.
    function frameAddOnForTerm(term) {
      if (TEMPLATE !== "mattress" || !lastFrameVariantId) return 0;
      var fq = quoteCache[lastFrameVariantId];
      if (!fq || !fq.insurable) return 0;
      var fplan = pickInsuranceForTerm(fq, term);
      return fplan ? premiumOf(fplan) : 0;
    }

    // The block renders the sidebar with mattress-only prices — it doesn't know
    // about DreamCloud's bundled frame. After the block fills the sidebar, fold
    // the frame's per-term premium into each option's displayed price (and the
    // second-chance total). Mattress template + a selected frame only.
    function patchSidebarFrameTotal() {
      try {
        if (TEMPLATE !== "mattress" || !lastFrameVariantId) return;
        var mq = quoteCache[currentVariantId()];
        if (!mq || !mq.insurable) return;
        var content = document.getElementById("loxa-shopify-sidebar-content");
        if (!content) return;
        var opts = content.querySelectorAll(
          ".loxa-sidebar-option[data-loxa-code]",
        );
        opts.forEach(function (opt) {
          var code = opt.getAttribute("data-loxa-code");
          var mPlan = mq.insurances.find(function (i) {
            return i.code === code;
          });
          if (!mPlan) return;
          var term = String(mPlan.insurance_term || mPlan.term || DEFAULT_TERM);
          var combined = premiumOf(mPlan) + frameAddOnForTerm(term);
          var priceEl = opt.querySelector(".loxa-sidebar-option-price");
          // Only write when the figure actually changes — otherwise our own
          // write would retrigger the sidebar observer in a loop.
          var target = "£" + combined.toFixed(2);
          if (priceEl && priceEl.textContent.trim() !== target) {
            priceEl.textContent = target;
          }
        });
      } catch (e) {
        notify("patchSidebarFrameTotal failed", e);
      }
    }

    // Watch the block's sidebar content; whenever it (re)renders the option
    // list, fold in the frame premium. Debounced, and patchSidebarFrameTotal is
    // idempotent (it overwrites prices with the combined figure), so re-running
    // on our own mutations is harmless.
    function attachSidebarObserver() {
      var content = document.getElementById("loxa-shopify-sidebar-content");
      if (!content || content._loxaDcObserved) return;
      content._loxaDcObserved = true;
      var debounce = null;
      try {
        var obs = new MutationObserver(function () {
          if (debounce) return;
          debounce = setTimeout(function () {
            debounce = null;
            patchSidebarFrameTotal();
          }, 50);
        });
        obs.observe(content, { childList: true, subtree: true });
      } catch (e) {
        notify("attachSidebarObserver failed", e);
      }
    }

    // The mattress's own premium for the current variant, from OUR quote cache
    // (authoritative — never read back from the title, which we mutate).
    function mattressBasePremium() {
      var vid = currentVariantId();
      var q = vid ? quoteCache[vid] : null;
      if (!q || !q.insurable) return null;
      var plan = pickInsuranceForTerm(q, DEFAULT_TERM);
      return plan ? premiumOf(plan) : null;
    }

    // Rewrite the £ figure in the block-rendered title to (mattress + frame).
    // CRITICAL: the base is derived from the quote cache, NOT parsed from the
    // title — parsing the title (which we just wrote) would compound the frame
    // premium every poll tick and send the price spiralling. Idempotent: same
    // inputs → same output, so re-running is a no-op.
    function applyFrameToTitle() {
      try {
        var el = titleEl();
        if (!el) return;
        var base = mattressBasePremium();
        if (base == null) return; // no mattress quote yet — let the block stand
        var combined = base + frameAddOn(); // frameAddOn() is 0 when no frame
        var target = "£" + combined.toFixed(2);
        // Only rewrite if the £ figure actually differs (avoids needless writes
        // + observer churn). Match the first £ figure in the title.
        var m = el.textContent.match(/£\s*([\d,]+(?:\.\d{1,2})?)/);
        if (!m) return;
        var current = parseFloat(m[1].replace(/,/g, ""));
        if (Math.abs(current - combined) < 0.005) return; // already correct
        el.textContent = el.textContent.replace(
          /£\s*[\d,]+(?:\.\d{1,2})?/,
          target,
        );
      } catch (e) {
        notify("applyFrameToTitle failed", e);
      }
    }

    // ── Variant + frame reads ─────────────────────────────────────────────────
    function currentVariantId() {
      try {
        // On bedframe/bundle pages the theme drives the variant via the URL
        // (?variant=) and soft-navigates — ShopifyAnalytics.selectedVariantId
        // and the form's id input lag behind. So prefer the URL, then fall back
        // to analytics, then the form. (Mattress keeps all three in sync, so
        // URL-first is correct there too.)
        var urlVid = new URLSearchParams(window.location.search).get("variant");
        if (urlVid) return String(urlVid);
        if (
          window.ShopifyAnalytics &&
          window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.selectedVariantId
        ) {
          return String(window.ShopifyAnalytics.meta.selectedVariantId);
        }
        var input = document.querySelector(
          'form[action="/cart/add"] input[name="id"]',
        );
        return input && input.value ? String(input.value) : null;
      } catch (e) {
        return null;
      }
    }
    function variantInfoFromAnalytics(variantId) {
      try {
        var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
        var prod = meta && meta.product;
        if (!prod || !Array.isArray(prod.variants)) return null;
        for (var i = 0; i < prod.variants.length; i++) {
          if (String(prod.variants[i].id) === String(variantId)) {
            return {
              title:
                prod.variants[i].name || prod.variants[i].public_title || "",
              pricePounds: (prod.variants[i].price || 0) / 100,
            };
          }
        }
      } catch (e) {}
      return null;
    }
    function getSelectedFrameInput() {
      try {
        var inputs = document.querySelectorAll(
          "[data-product-mattress-bundler-single-select-checkbox]",
        );
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].checked && inputs[i].value && inputs[i].value !== "0") {
            return inputs[i];
          }
        }
      } catch (e) {}
      return null;
    }

    // ALL checked bundler add-ons the customer selected (frames AND bedding,
    // covers, duvets, protectors, etc.). The bundler tags every selectable
    // add-on with [data-product-mattress-bundler-input]; the frame group also
    // carries -single-select-checkbox, bedding/covers do not. When we take over
    // the native add we MUST add every one of these — collecting only the frame
    // silently dropped the customer's bedding/covers (broke core add-to-cart).
    function getSelectedBundlerInputs() {
      var out = [];
      try {
        var inputs = document.querySelectorAll(
          "[data-product-mattress-bundler-input]",
        );
        for (var i = 0; i < inputs.length; i++) {
          var inp = inputs[i];
          if (inp.checked && inp.value && inp.value !== "0") out.push(inp);
        }
      } catch (e) {}
      return out;
    }

    // ── Quote fetch / cache ────────────────────────────────────────────────────
    // Resolve a variant's authoritative title + price. Prefer explicit info,
    // then page analytics — but on a bedframe Base soft-nav, analytics still
    // describes the OLD product, so the new variant isn't found and price is 0,
    // which makes the proxy return a non-insurable/empty quote (the "price
    // doesn't change / NO CARD" bug). Fall back to /variants/{id}.js for the
    // real price (mirrors loxa-nectar.js).
    function resolveVariantInfo(variantId, info) {
      var fromAnalytics = variantInfoFromAnalytics(variantId);
      // A clean title comes from explicit info, then page analytics — NEVER from
      // scraped label chrome (frameLabelInfo deliberately leaves title empty).
      var cleanTitle =
        (info && info.title) || (fromAnalytics && fromAnalytics.title) || "";
      // Trust a supplied price (analytics can describe the OLD product on a
      // bedframe soft-nav, giving a 0 price → empty/non-insurable quote, the
      // "price doesn't change / NO CARD" bug). But still resolve a real title.
      if (
        info &&
        typeof info.pricePounds === "number" &&
        info.pricePounds > 0
      ) {
        if (cleanTitle) {
          return Promise.resolve({
            title: cleanTitle,
            pricePounds: info.pricePounds,
          });
        }
        // Price is good but we have no clean title — fetch the real name,
        // keeping the supplied price.
        return fetch(shopRoot() + "/variants/" + variantId + ".js")
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (v) {
            return {
              title: (v && (v.name || v.public_title)) || "",
              pricePounds: info.pricePounds,
            };
          })
          .catch(function () {
            return { title: "", pricePounds: info.pricePounds };
          });
      }
      if (fromAnalytics && fromAnalytics.pricePounds > 0) {
        return Promise.resolve(fromAnalytics);
      }
      return fetch(shopRoot() + "/variants/" + variantId + ".js")
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (v) {
          if (!v) return info || fromAnalytics || {};
          return {
            title: v.name || v.public_title || cleanTitle || "",
            pricePounds: typeof v.price === "number" ? v.price / 100 : 0,
          };
        })
        .catch(function () {
          return info || fromAnalytics || {};
        });
    }

    function fetchQuoteFor(variantId, info) {
      if (!variantId) return Promise.resolve(null);
      if (quoteCache[variantId]) return Promise.resolve(quoteCache[variantId]);
      return resolveVariantInfo(variantId, info).then(function (ctx) {
        var pricePounds = (ctx && ctx.pricePounds) || 0;
        // Pass the resolved price (pence→pounds) to core's data API.
        return Promise.resolve(
          Dev.fetchInsurance(
            String(variantId),
            (ctx && ctx.title) || "",
            pricePounds,
          ),
        )
          .then(function (resp) {
            var q = normaliseQuote(
              resp,
              variantId,
              pricePounds,
              (ctx && ctx.title) || "",
            );
            if (q) quoteCache[variantId] = q;
            // If a frame is selected, fold its premium into the block's title.
            applyFrameToTitle();
            return q;
          })
          .catch(function (e) {
            notify("fetchQuoteFor failed", e, { variantId: variantId });
            return null;
          });
      });
    }

    // ── ATC button enable/disable (during async frame quote) ───────────────────
    function atcButtons() {
      return Array.prototype.slice.call(
        document.querySelectorAll(
          'form[action="/cart/add"] button[type="submit"], .product-sticky-atc__btn',
        ),
      );
    }
    function disableAtc() {
      atcButtons().forEach(function (b) {
        if (b.disabled) return;
        b.disabled = true;
        b.setAttribute(ATC_DISABLED_ATTR, "1");
      });
    }
    function enableAtc() {
      atcButtons().forEach(function (b) {
        if (b.getAttribute(ATC_DISABLED_ATTR) === "1") {
          b.disabled = false;
          b.removeAttribute(ATC_DISABLED_ATTR);
        }
      });
    }

    // ── Frame handling (mattress only) ─────────────────────────────────────────
    // Read the selected frame's title + add-on price from its bundler label.
    // The frame is a DIFFERENT product not on this PDP's analytics, so this is
    // the only authoritative source for its title/price — used both to pre-warm
    // the quote and to build complete insurance props at ATC time.
    function frameLabelInfo(input) {
      // Parse ONLY the price from the bundler label. The label's textContent is
      // selector chrome ("Luxury Divan Bed Select bed frame +£609") — never a
      // clean product title — so we leave title empty and let resolveVariantInfo
      // resolve the real name from analytics / /variants/{id}.js.
      var label = input && input.closest ? input.closest("label") : null;
      var price = 0;
      if (label) {
        var m = label.textContent.match(/\+£\s*([\d,]+(?:\.\d{1,2})?)/);
        if (m) price = parseFloat(m[1].replace(/,/g, ""));
      }
      return { title: "", pricePounds: price };
    }

    // Pre-warm the frame quote so the ATC handler can stamp it synchronously.
    function handleFrameChange() {
      try {
        var input = getSelectedFrameInput();
        var frameId = input ? String(input.value) : null;
        if (frameId === lastFrameVariantId) {
          enableAtc();
          return;
        }
        lastFrameVariantId = frameId;
        if (!frameId) {
          enableAtc();
          return;
        }
        disableAtc();
        var info = frameLabelInfo(input);
        fetchQuoteFor(frameId, {
          title: info.title,
          pricePounds: info.pricePounds,
        })
          .then(function () {
            enableAtc();
          })
          .catch(function () {
            enableAtc();
          });
      } catch (e) {
        notify("handleFrameChange failed", e);
        enableAtc();
      }
    }
    function bindFrameInputs() {
      try {
        var inputs = document.querySelectorAll(
          "[data-product-mattress-bundler-single-select-checkbox]",
        );
        inputs.forEach(function (inp) {
          if (inp._loxaDcBound) return;
          inp._loxaDcBound = true;
          inp.addEventListener("change", handleFrameChange);
        });
        var swatches = document.querySelectorAll(
          "[data-product-mattress-bundler-colour]",
        );
        swatches.forEach(function (sw) {
          if (sw._loxaDcBound) return;
          sw._loxaDcBound = true;
          sw.addEventListener("click", function () {
            setTimeout(handleFrameChange, 100);
          });
        });
      } catch (e) {
        notify("bindFrameInputs failed", e);
      }
    }

    // ── Form-submit ATC interceptor (the non-AJAX add) ─────────────────────────
    // DreamCloud's add is a native form POST → /cart page. We intercept the
    // submit, and if insurance is ticked, cancel it, add product + insurance
    // line(s) ourselves (each pair sharing a deterministic _loxaLinkId), then
    // navigate to /cart. If the checkbox is off, the native submit proceeds
    // UNCHANGED (zero behaviour change). Any throw → fall back to native submit.
    function installFormAddInterceptor() {
      var form = document.querySelector('form[action="/cart/add"]');
      if (!form || form._loxaDcSubmitBound) return;
      form._loxaDcSubmitBound = true;

      form.addEventListener(
        "submit",
        function (e) {
          if (form._loxaDcSubmitting) return; // re-entry from our fallback
          var checkbox = document.getElementById(CHECKBOX_ID);
          var selected = checkbox && checkbox.checked;

          // No protection selected → do NOTHING. The core block (booted with
          // disable_main_flow OFF) binds its OWN Second Chance to the ATC button
          // click (setupSecondChanceAtc) and shows the prompt + bypass. If WE
          // also intercepted here we'd open a second sidebar / double-fire. So we
          // only act when protection IS selected (to do the insured add); the
          // unselected path is left entirely to the block + native submit.
          if (!selected) return; // native path, block owns second chance

          try {
            e.preventDefault();
            e.stopPropagation();
            doInsuredAdd(form);
          } catch (err) {
            notify("form submit interceptor failed — native fallback", err);
            // Fall back to native submit so ATC never breaks.
            form._loxaDcSubmitting = true;
            try {
              form.submit();
            } catch (e2) {
              notify("native fallback submit failed", e2);
            }
          }
        },
        true, // capture: run before theme/bundler handlers
      );
    }

    // NOTE: Second Chance (prompt when ATC is clicked with no protection) is
    // owned ENTIRELY by the core block — it binds its own handler to the ATC
    // button (setupSecondChanceAtc) when booted with disable_main_flow OFF.
    // We deliberately do NOT replicate it here; doing so opened a second sidebar
    // and double-fired. Our interceptor only acts when protection IS selected.

    // The term the customer actually chose in the block's sidebar/card. The
    // block renders BOTH the 3yr + 5yr options and tracks the selection in
    // LoxaShopify.selectedInsuranceCode; getSelectedInsurance() returns the
    // chosen plan. We honour that term (not a hardcoded default) so a 3yr
    // pick adds a 3yr line. Falls back to DEFAULT_TERM if the block hasn't
    // recorded a selection (e.g. checkbox ticked before the sidebar opened).
    function selectedTerm() {
      try {
        var block = window.LoxaShopify;
        var sel =
          block && typeof block.getSelectedInsurance === "function"
            ? block.getSelectedInsurance()
            : null;
        if (sel) {
          var t = sel.insurance_term || sel.term;
          if (t) return String(t);
        }
      } catch (e) {}
      return DEFAULT_TERM;
    }

    // Build the product line(s) from the form, attach insurance lines, POST
    // sequentially (insurance-first/product-second per pair so the product
    // renders above its insurance line), then navigate to /cart.
    function doInsuredAdd(form) {
      var term = selectedTerm();
      var mattressVid = currentVariantId();
      var qtyInput = form.querySelector('input[name="quantity"]');
      var qty =
        qtyInput && qtyInput.value ? parseInt(qtyInput.value, 10) || 1 : 1;

      // Collect EVERY product line the native form would add — we replace the
      // native submit, so dropping any line breaks the customer's order.
      //   mattress → the mattress + every selected bundler add-on (frame, bedding,
      //              covers, duvet, protector…); each insurable add-on is insured.
      //   bedframe → the single product variant.
      // (BUNDLE template is a no-op — see pdpTemplate(); bundle PDPs never init.)
      var productLines = [
        {
          variantId: mattressVid,
          quantity: qty,
          info: variantInfoFromAnalytics(mattressVid),
        },
      ];
      if (TEMPLATE === "mattress") {
        getSelectedBundlerInputs().forEach(function (inp) {
          productLines.push({
            variantId: String(inp.value),
            quantity: qty,
            info: frameLabelInfo(inp), // label → { title, pricePounds }
          });
        });
      }

      // For each product line, resolve its quote + insurance variant, build the
      // pair (insurance line then product line), preserving order.
      var pairPromises = productLines.map(function (line, idx) {
        return fetchQuoteFor(line.variantId, line.info).then(function (quote) {
          if (!quote || !quote.insurable) {
            // Uninsurable line — add product only, no insurance.
            return { product: line, insurance: null };
          }
          var plan = pickInsuranceForTerm(quote, term);
          if (!plan || premiumOf(plan) <= 0) {
            return { product: line, insurance: null };
          }
          return resolveInsuranceVariantId(plan).then(function (insVid) {
            if (!insVid) {
              return { product: line, insurance: null };
            }
            var linkId = linkIdFor(line.variantId, plan.code);
            line.linkId = linkId;
            var props = buildInsuranceProps(
              plan,
              quote,
              insVid,
              line.variantId,
              linkId,
            );
            return {
              product: line,
              insurance: {
                variantId: insVid,
                quantity: line.quantity,
                properties: props,
              },
            };
          });
        });
      });

      Promise.all(pairPromises)
        .then(function (pairs) {
          // Build ONE items[] array (per pair: insurance then product) and add
          // it in a SINGLE /cart/add call. One request = Bespoke runs its
          // mutation pass once on the complete cart, instead of interleaving
          // between separate adds and stripping our insurance props.
          var items = [];
          pairs.forEach(function (pair) {
            if (pair.insurance) {
              items.push({
                id: parseInt(pair.insurance.variantId, 10),
                quantity: pair.insurance.quantity,
                properties: pair.insurance.properties,
              });
            }
            var pProps = {};
            if (pair.product.linkId) pProps._loxaLinkId = pair.product.linkId;
            items.push({
              id: parseInt(pair.product.variantId, 10),
              quantity: pair.product.quantity,
              properties: pProps,
            });
          });
          return addLinesRaw(items);
        })
        .then(function () {
          // Navigate to /cart (replicates the native redirect). Reconcile-on-
          // load fixes any missing insurance line.
          window.location.href = shopRoot() + "/cart";
        })
        .catch(function (e) {
          notify("doInsuredAdd failed — native fallback", e);
          form._loxaDcSubmitting = true;
          try {
            form.submit();
          } catch (e2) {
            notify("native fallback submit failed", e2);
          }
        });
    }

    // ── #MainContent swap watcher (product/variant swaps) ──────────────────────
    // Runs for ALL templates. Mattress = material swap; BEDFRAME = the "Base" /
    // Style selector soft-navigates to a different product, replacing part of
    // #MainContent. On any such swap we reset variant state + re-assert the
    // anchor so the new product re-quotes and the block re-renders its card.
    // We observe the SUBTREE (not just direct children) because the theme
    // replaces a node deep inside #MainContent on a Base swap, and we react to
    // a real change (anchor gone OR variant changed) rather than the raw
    // mutation, so unrelated DOM churn doesn't thrash.
    function attachMainContentObserver() {
      var main = document.getElementById("MainContent");
      if (!main || main._loxaDcObserved) return;
      main._loxaDcObserved = true;
      var debounce = null;
      try {
        var obs = new MutationObserver(function () {
          if (debounce) return;
          debounce = setTimeout(function () {
            debounce = null;
            try {
              var anchorGone = !document.getElementById(ANCHOR_ID);
              var vidChanged = currentVariantId() !== lastVariantId;
              if (!anchorGone && !vidChanged) return; // nothing relevant changed
              // Reset variant state so the (possibly new) product re-quotes on
              // the next poll, and re-assert the anchor + interceptor. The block
              // re-renders its card into the anchor (repoked by ensureBoxLayout).
              lastVariantId = null;
              lastFrameVariantId = null;
              injectAnchor();
              installFormAddInterceptor();
              if (TEMPLATE === "mattress") bindFrameInputs();
            } catch (e) {
              notify("MainContent re-init failed", e);
            }
          }, 300);
        });
        obs.observe(main, { childList: true, subtree: true });
      } catch (e) {
        notify("attachMainContentObserver failed", e);
      }
    }

    // Drive the core block to re-render its card (and the sidebar, if open) for
    // the CURRENT variant using OUR fresh quote's raw response — the exact shape
    // the block's render expects. The block doesn't re-quote per variant on the
    // bedframe Base/Size soft-nav, so its own data goes stale; feeding it our
    // fresh raw response keeps the card + sidebar correct WITHOUT a page reload.
    function renderBlockForVariant(vid) {
      try {
        var block = window.LoxaShopify;
        var q = vid ? quoteCache[vid] : null;
        if (!block) return;
        // Non-insurable variant (or no quote): e.g. the bedframe "Base" swap to
        // a sub-threshold product like the £99 Platform Bed, which the proxy
        // returns insurable:false. The block's render has no "non-insurable →
        // remove" path that fires reliably here, so the PREVIOUS insurable
        // base's card persists and shows a stale price. Tear the card down
        // ourselves and collapse the box so a non-insurable base shows nothing.
        if (!q || !q.raw || !q.insurable) {
          var staleOffer = document.getElementById(OFFER_ID);
          if (staleOffer) staleOffer.remove();
          block.lastInsuranceData = q ? q.raw : null;
          block.insuranceProducts = [];
          var anchor = document.getElementById(ANCHOR_ID);
          if (anchor) anchor.style.display = "none";
          return;
        }
        if (typeof block.renderInsuranceOffer === "function") {
          block.renderInsuranceOffer(q.raw, {});
          // The card render reads q.raw but doesn't write it back to the block's
          // own state. The sidebar (loadSidebarContent) and its selection reads
          // pull from block.lastInsuranceData / block.insuranceProducts, so on a
          // cold-boot race (block quoted the variants-array first entry before
          // the URL settled to the selected variant) the sidebar would show a
          // staler variant than our card. Push our authoritative quote into the
          // block's data so the sidebar can never disagree with the card.
          block.lastInsuranceData = q.raw;
          block.insuranceProducts = Array.isArray(q.raw.insurances)
            ? q.raw.insurances.slice()
            : [];
        }
        // If the sidebar is open, re-render its content for the new variant too.
        var sidebar = document.getElementById("loxa-shopify-sidebar");
        if (
          sidebar &&
          sidebar.classList.contains("loxa-sidebar-open") &&
          typeof block.loadSidebarContent === "function"
        ) {
          block.loadSidebarContent(sidebar, q.raw);
          patchSidebarFrameTotal();
        }
      } catch (e) {
        notify("renderBlockForVariant failed", e);
      }
    }

    // ── Variant-change poll: re-assert anchor + warm quote + frame title ───────
    function poll() {
      try {
        // Install the render wrapper once the block has booted (idempotent).
        // The block may load after our boot, so arm it from the poll.
        wrapBlockRender();
        // Re-assert the anchor if a content swap (Dynamic Yield/ConvertFlow)
        // removed it; the block re-renders its card into it.
        if (!document.getElementById(ANCHOR_ID)) {
          injectAnchor();
          installFormAddInterceptor();
        }
        // Base/product swap: the bedframe "Base" selector soft-navigates to a
        // DIFFERENT product (path changes). The block keeps its stale quote, and
        // our quoteCache may hold a wrong entry written mid-swap. Clear our cache
        // + variant state so every line re-quotes fresh against the new product.
        if (window.location.pathname !== lastProductPath) {
          lastProductPath = window.location.pathname;
          quoteCache = {};
          lastVariantId = null;
          lastFrameVariantId = null;
        }
        // Pull the block's card inside the anchor box once it has rendered.
        ensureBoxLayout();
        var vid = currentVariantId();
        if (vid && vid !== lastVariantId) {
          lastVariantId = vid;
          // Fetch THIS variant's fresh quote, then drive the block to re-render
          // its card + (if open) sidebar from OUR raw response, and repaint the
          // badge. The block doesn't re-quote per variant on the soft-nav, so
          // its own card/sidebar go stale; feeding it our fresh quote keeps both
          // correct in place — no reload. Mattress also refreshes an open sidebar.
          var paintVid = vid;
          (quoteCache[vid]
            ? Promise.resolve(quoteCache[vid])
            : fetchQuoteFor(vid)
          ).then(function (q) {
            if (currentVariantId() !== paintVid) return;
            renderBlockForVariant(paintVid);
            applyFrameToTitle();
          });
        }
        if (TEMPLATE === "mattress") {
          bindFrameInputs();
          handleFrameChange();
        }
        // Keep the title in sync with our own quote (no frame add-on outside
        // mattress). Cheap + idempotent — only rewrites when the £ differs.
        applyFrameToTitle();
      } catch (e) {
        notify("poll failed", e);
      }
    }

    // ── Boot the PDP layer ─────────────────────────────────────────────────────
    injectStyles();
    injectAnchor(); // the core block (bundled mode) renders its card in here
    installFormAddInterceptor();
    // Swap watcher runs for all templates (bedframe Base/Style swap too).
    attachMainContentObserver();
    if (TEMPLATE === "mattress") {
      bindFrameInputs();
      attachSidebarObserver();
    }
    // Warm the quote for the initial variant.
    var initialVid = currentVariantId();
    if (initialVid) {
      lastVariantId = initialVid;
      fetchQuoteFor(initialVid).then(function (q) {
        renderBlockForVariant(initialVid);
        applyFrameToTitle();
      });
    }
    setInterval(poll, 200);
  }

  function pdpTemplate() {
    var body = document.body;
    var isProduct = /\/products\/[^/?#]+/.test(window.location.pathname);
    if (!body || !isProduct) return "unknown";
    // Bundle pages are NO-OP for now: the bundle is added by a 3rd-party bundle
    // app (bundler.js) that expands it into component items via its own ATC
    // flow. Intercepting it dropped the bundle's items, so we leave bundle PDPs
    // entirely untouched (no offer, no interception). Treated as "unknown" so
    // initPdp() early-returns. Revisit if bundle insurance is needed later.
    if (body.classList.contains("product--template-bundle")) return "unknown";
    if (body.classList.contains("product--template-bedframe"))
      return "bedframe";
    if (body.classList.contains("product--template-2026")) return "mattress";
    return "unknown";
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CART LAYER (/cart page)
  // ═══════════════════════════════════════════════════════════════════════════
  function initCart() {
    if (window.__loxaDcCartInstalled) return;
    window.__loxaDcCartInstalled = true;
    log("cart init");

    injectStyles(); // cart-line un-hide + cart-side CTA styling
    installAtomicOverrides();
    installBespokeRepair();
    reconcileOnLoad();
    mountCartToggle();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CART TOGGLE — "Protect your basket" (/cart page)
  // ═══════════════════════════════════════════════════════════════════════════
  // One toggle above the checkout button that insures every uninsured insurable
  // basket line in one click, adding SEPARATE insurance lines (the same shape
  // the PDP produces, joined by _loxaLinkId) via the existing addLinesRaw. OFF
  // is a clean sweep of all insurance. See Details shows 5yr + 3yr basket totals.
  //
  // Reuses the module-scoped helpers (readCart, pickInsuranceForTerm, premiumOf,
  // isInsuranceLine, normaliseQuote, resolveInsuranceVariantId, buildInsuranceProps,
  // linkIdFor, addLinesRaw, updateLineQuantitiesRaw, refreshCartSections, notify).
  // The PDP's fetchQuoteFor is NOT reachable here (nested in initPdp, coupled to
  // applyFrameToTitle), so the cart layer has its own minimal fetchCartQuote.

  var CART_CTA_ID = "loxa-dc-cart-cta";
  var cartToggleState = "idle";
  var cartQuote = null; // last quoteBasket() result
  var cartQuoteCache = {}; // variantId -> normalised quote (page lifetime)
  var selectedTerm = DEFAULT_TERM; // "5" (default) or "3" — sidebar term choice

  function poundsFromPence(p) {
    return "£" + (p / 100).toFixed(2);
  }

  function emptyBasketQuote() {
    return {
      uninsuredInsurable: [],
      alreadyInsured: [],
      total5yr: 0,
      total3yr: 0,
      protectTotal5yr: 0,
      protectTotal3yr: 0,
      insuredPremiumPence: 0,
      insuredTerms: [],
      anyInsurable: false,
    };
  }

  // The protect total (pence) for the currently-selected term.
  function protectTotalForSelected() {
    if (!cartQuote) return 0;
    return selectedTerm === "3"
      ? cartQuote.protectTotal3yr
      : cartQuote.protectTotal5yr;
  }
  function termLabel(t) {
    return (t || selectedTerm) === "3" ? "3 year" : "5 year";
  }

  // Cart-layer quote fetch. The cart line carries an authoritative title +
  // price, so unlike the PDP path no /variants fallback is needed — call the
  // core data API + the module-scoped normaliseQuote directly.
  function fetchCartQuote(variantId, title, pricePounds) {
    if (!variantId) return Promise.resolve(null);
    if (cartQuoteCache[variantId]) {
      return Promise.resolve(cartQuoteCache[variantId]);
    }
    var Dev = window.LoxaShopifyDev;
    if (!Dev || typeof Dev.fetchInsurance !== "function") {
      notify("fetchCartQuote: LoxaShopifyDev.fetchInsurance unavailable", null, {
        variantId: variantId,
      });
      return Promise.resolve(null);
    }
    return Promise.resolve(
      Dev.fetchInsurance(String(variantId), title || "", pricePounds || 0),
    )
      .then(function (resp) {
        var q = normaliseQuote(resp, variantId, pricePounds || 0, title || "");
        if (q) cartQuoteCache[variantId] = q;
        return q;
      })
      .catch(function (e) {
        notify("fetchCartQuote failed", e, { variantId: variantId });
        return null;
      });
  }

  // A product line is "already insured" if the cart holds an insurance line
  // joined to it by _loxaLinkId (fallback _insuredProductId) — the same join
  // reconcileOnLoad uses. Returns a map of insured product cart-line key -> true.
  function insuredProductKeys(cart) {
    var insuredKeys = {};
    var byLink = {};
    var byVariant = {};
    cart.items.forEach(function (it) {
      if (isInsuranceLine(it)) return;
      var p = it.properties || {};
      if (p._loxaLinkId) byLink[p._loxaLinkId] = it.key;
      byVariant[String(it.variant_id)] = it.key;
    });
    cart.items.forEach(function (it) {
      if (!isInsuranceLine(it)) return;
      var p = it.properties || {};
      var key =
        (p._loxaLinkId && byLink[p._loxaLinkId]) ||
        (p._insuredProductId && byVariant[p._insuredProductId]) ||
        null;
      if (key) insuredKeys[key] = true;
    });
    return insuredKeys;
  }

  // Read the cart, partition lines, quote every uninsured product line, and
  // total the 5yr + 3yr premiums (pence) over the insurable uninsured lines.
  function quoteBasket() {
    return readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          return emptyBasketQuote();
        }
        var insured = insuredProductKeys(cart);
        // Premiums already in the basket (insurance lines): line price (pence)
        // × qty IS the premium. Summed so the toggle can show the SAME total
        // whether OFF (about to add) or ON (already protected). Also collect the
        // DISTINCT terms present on existing insurance lines, so the toggle can
        // tell when the basket needs normalising to a single chosen term.
        var insuredPremiumPence = 0;
        var insuredTermsMap = {};
        cart.items.forEach(function (it) {
          if (isInsuranceLine(it)) {
            insuredPremiumPence += (it.price || 0) * (it.quantity || 1);
            var lt = it.properties && it.properties._loxaTerm;
            if (lt) insuredTermsMap[String(lt)] = true;
          }
        });
        var insuredTerms = Object.keys(insuredTermsMap);
        var uninsuredProductLines = cart.items.filter(function (it) {
          return !isInsuranceLine(it) && !insured[it.key];
        });
        var alreadyInsured = cart.items
          .filter(function (it) {
            return !isInsuranceLine(it) && insured[it.key];
          })
          .map(function (it) {
            return { item: it };
          });
        return Promise.all(
          uninsuredProductLines.map(function (it) {
            return fetchCartQuote(
              String(it.variant_id),
              // it.title is the combined product + variant display title
              // ("Hybrid Mattress - King"); product_title drops the variant.
              // Matches the PDP path, which uses the variant name.
              it.title || it.product_title,
              (it.price || 0) / 100,
            ).then(function (q) {
              return { item: it, quote: q };
            });
          }),
        ).then(function (results) {
          var uninsuredInsurable = [];
          var total5yr = 0;
          var total3yr = 0;
          results.forEach(function (r) {
            var q = r.quote;
            if (!q || !q.insurable) return; // non-insurable → excluded
            var pick5yr = pickInsuranceForTerm(q, "5");
            var pick3yr = pickInsuranceForTerm(q, "3");
            if (!pick5yr) return; // no usable plan → excluded
            uninsuredInsurable.push({
              item: r.item,
              quote: q,
              pick5yr: pick5yr,
              pick3yr: pick3yr,
            });
            var qty = r.item.quantity || 1;
            total5yr += Math.round(premiumOf(pick5yr) * 100) * qty;
            if (pick3yr) {
              total3yr += Math.round(premiumOf(pick3yr) * 100) * qty;
            }
          });
          return {
            uninsuredInsurable: uninsuredInsurable,
            alreadyInsured: alreadyInsured,
            total5yr: total5yr,
            total3yr: total3yr,
            // The whole basket's protection premium per term — uninsured (to
            // add) PLUS already-insured. Shown the SAME in both toggle states
            // ("Protect basket for £X" / "Protected for £X"). The insured
            // premium is whatever term those lines were added at; for display
            // we fold it into both so the figure stays stable across the toggle.
            protectTotal5yr: total5yr + insuredPremiumPence,
            protectTotal3yr: total3yr + insuredPremiumPence,
            // Actual premium total of the insurance lines already in the basket
            // (whatever terms they're at). Used for the mixed-term label, where
            // neither protectTotal3yr nor protectTotal5yr reflects reality.
            insuredPremiumPence: insuredPremiumPence,
            insuredTerms: insuredTerms, // distinct terms on existing ins. lines
            anyInsurable:
              uninsuredInsurable.length > 0 || alreadyInsured.length > 0,
          };
        });
      })
      .catch(function (e) {
        notify("quoteBasket failed", e);
        return emptyBasketQuote();
      });
  }

  // ── CTA render ─────────────────────────────────────────────────────────────
  function injectCta(anchorEl) {
    if (!anchorEl || !anchorEl.parentNode) return null;
    var existing = document.getElementById(CART_CTA_ID);
    if (existing) return existing;
    var cta = document.createElement("div");
    cta.id = CART_CTA_ID;
    cta.className = "loxa-dc-cart";
    cta.innerHTML =
      '<div class="loxa-dc-cart__heading">Protect your basket</div>' +
      '<div class="loxa-dc-cart__row">' +
      '<label class="loxa-dc-cart__toggle">' +
      '<input type="checkbox" class="loxa-dc-cart__input" />' +
      '<span class="loxa-dc-cart__track"></span>' +
      '<span class="loxa-dc-cart__label"></span>' +
      "</label>" +
      '<button type="button" class="loxa-dc-cart__details">See details</button>' +
      "</div>";
    // Insert AFTER the anchor (below the checkout button).
    anchorEl.parentNode.insertBefore(cta, anchorEl.nextSibling);
    cta
      .querySelector(".loxa-dc-cart__input")
      .addEventListener("change", function (e) {
        if (e.target.checked) {
          applyBasket(selectedTerm);
        } else {
          removeAllInsurance();
        }
      });
    cta
      .querySelector(".loxa-dc-cart__details")
      .addEventListener("click", function (e) {
        e.preventDefault();
        openCartSidebar();
      });
    return cta;
  }

  function renderCtaState() {
    var cta = document.getElementById(CART_CTA_ID);
    if (!cta) return;
    cta.setAttribute("data-state", cartToggleState);
    var label = cta.querySelector(".loxa-dc-cart__label");
    var input = cta.querySelector(".loxa-dc-cart__input");
    if (!label || !input) return;
    switch (cartToggleState) {
      case "quoting":
        label.textContent = "Calculating protection…";
        input.checked = false;
        break;
      case "ready_off":
        label.innerHTML =
          "Add " +
          termLabel() +
          " protection - <strong>" +
          poundsFromPence(protectTotalForSelected()) +
          "</strong>";
        input.checked = false;
        break;
      case "applying":
        label.textContent = "Adding protection…";
        break;
      case "ready_on":
        // Mixed-term basket (insurance lines at >1 distinct term): the per-term
        // totals don't reflect reality, and there's no single term to name, so
        // drop the term and show the actual insured premium.
        if (cartQuote && (cartQuote.insuredTerms || []).length > 1) {
          label.innerHTML =
            "Protected - <strong>" +
            poundsFromPence(cartQuote.insuredPremiumPence || 0) +
            "</strong>";
        } else {
          label.innerHTML =
            termLabel() +
            " protection - <strong>" +
            poundsFromPence(protectTotalForSelected()) +
            "</strong>";
        }
        input.checked = true;
        break;
      case "removing":
        label.textContent = "Removing protection…";
        break;
      case "error_off":
        label.innerHTML =
          '<span class="loxa-dc-cart__error">Couldn\'t add protection — try again</span>';
        input.checked = false;
        break;
      case "error_on":
        label.innerHTML =
          '<span class="loxa-dc-cart__error">Couldn\'t remove protection — try again</span>';
        input.checked = true;
        break;
    }
  }

  // ── Mount + re-render observer ───────────────────────────────────────────────
  // The CTA mounts BELOW the checkout button, OUTSIDE the cart summary block
  // (`#main-cart-summary`). The summary is re-rendered by the bespoke-shipping
  // app ("Recalculating cart…"), which would destroy a CTA mounted inside it;
  // mounting after the checkout CTA wrapper keeps us clear of that churn and
  // simplifies the UX (no fighting the re-render). The CTA is inserted as the
  // NEXT sibling of the returned anchor — see injectCta.
  //
  // Gotcha: there are TWO `[name="checkout"]` elements; the first in DOM order
  // is the hidden header add-to-cart drawer button (inside <cart-notification>).
  // Never anchor inside that subtree or the CTA renders invisible.
  // Find the element to mount the CTA after. Strategy: locate the REAL checkout
  // button (visible, not the hidden header <cart-notification> drawer), then
  // mount after its nearest sensible block so the toggle sits just below
  // Checkout. We deliberately anchor on the checkout button because it reliably
  // exists on this theme (the summary panel id varies); injectCta inserts the
  // CTA as the next sibling of whatever we return.
  function findCartAnchor() {
    // 1. The real checkout control — try several selectors, skip the header drawer.
    var checkout = null;
    var sels = [
      "#checkout",
      '[name="checkout"]',
      ".cart__checkout-button",
      'a[href="/checkout"]',
      'form[action="/cart"] [type="submit"]',
    ];
    for (var s = 0; s < sels.length && !checkout; s++) {
      var nodes = document.querySelectorAll(sels[s]);
      for (var i = 0; i < nodes.length; i++) {
        if (!nodes[i].closest("cart-notification")) {
          checkout = nodes[i];
          break;
        }
      }
    }
    if (checkout) {
      // Prefer the checkout's wrapper row (.cart__ctas) so we land just below the
      // whole button block; else anchor on the button itself.
      var ctaWrap = checkout.closest(".cart__ctas");
      return ctaWrap || checkout;
    }
    // 2. Fallbacks if no checkout button found at all.
    var ctas = document.querySelector(".cart__ctas");
    if (ctas && !ctas.closest("cart-notification")) return ctas;
    var footer = document.querySelector(".cart__footer");
    if (footer && !footer.closest("cart-notification")) return footer;
    return null;
  }

  // Single re-entrant entry point: re-quote, derive state, inject-or-hide, render.
  // Only shows the "quoting" skeleton on the FIRST run (no CTA present yet) — on
  // observer-driven re-derives we keep the current CTA visible and only repaint
  // when the derived state actually changes, so we never flicker.
  function refreshCartToggle() {
    var first = !document.getElementById(CART_CTA_ID);
    if (first) {
      cartToggleState = "quoting";
    }
    return quoteBasket()
      .then(function (q) {
        cartQuote = q;
        if (!q.anyInsurable) {
          var node = document.getElementById(CART_CTA_ID);
          if (node) node.remove();
          cartToggleState = "hidden";
          return;
        }
        // Reconcile selectedTerm with what's ACTUALLY in the basket. Without
        // this, a page load after a 3yr apply leaves selectedTerm at its "5"
        // default — the label reads "5 year" on a 3yr cart, and a subsequent
        // toggle/apply silently re-normalises the basket to 5yr. Only adopt the
        // basket's term when it's uniformly a single term; mixed baskets keep
        // the current selection so applying re-normalises them.
        if (q.insuredTerms && q.insuredTerms.length === 1) {
          selectedTerm = String(q.insuredTerms[0]) === "3" ? "3" : "5";
        }
        var nextState =
          q.uninsuredInsurable.length === 0 ? "ready_on" : "ready_off";
        var anchor = findCartAnchor();
        if (!anchor) {
          log("cart toggle: no anchor found — cannot mount");
          return;
        }
        injectCta(anchor);
        cartToggleState = nextState;
        renderCtaState();
      })
      .catch(function (e) {
        notify("refreshCartToggle failed", e);
      });
  }

  function mountCartToggle() {
    // Use the same cart-page gate as boot() (isCartPage) rather than a strict
    // "/cart" check, so locale prefixes / trailing slashes don't slip past.
    if (!isCartPage()) return;
    if (window.__loxaDcCartToggleMounted) return;
    window.__loxaDcCartToggleMounted = true;

    refreshCartToggle();

    // The CTA now mounts BELOW the checkout button, OUTSIDE `#main-cart-summary`,
    // so the bespoke-shipping summary re-render no longer destroys it. The
    // observer's job is just to re-derive when cart CONTENTS change (item
    // add/remove), so watch a broad stable container. `onlyOurs` skips our own
    // mutations; the 250ms debounce + `injectCta` idempotency prevent churn.
    var root =
      document.querySelector("#MainContent") ||
      document.querySelector("main") ||
      document.body;
    var pending = null;
    var obs = new MutationObserver(function (mutations) {
      // Ignore mutations caused by our own CTA (inject / re-render) — otherwise
      // injecting the toggle into the observed cart subtree retriggers this
      // observer → re-quote → re-render → flicker loop.
      var onlyOurs = mutations.every(function (m) {
        var t = m.target;
        return !!(
          t &&
          ((t.id === CART_CTA_ID) ||
            (t.closest && t.closest("#" + CART_CTA_ID)))
        );
      });
      if (onlyOurs) return;
      // Never re-derive while OUR own cart write is mid-flight — the in-place
      // refresh + the sweep/add mutate the observed DOM, and re-quoting against
      // a half-written cart is exactly what caused the mixed-term confusion.
      if (cartWriteInFlight) return;
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        if (cartWriteInFlight) return;
        refreshCartToggle();
      }, 250);
    });
    try {
      obs.observe(root, { childList: true, subtree: true });
    } catch (e) {
      notify("mountCartToggle observer failed", e);
    }
  }

  // ── Cart writes: in-place refresh, single-flight lock, term normalisation ─────
  //
  // The cart toggle does ALL its writes here. Two hard rules learned the hard
  // way on this theme:
  //  1. NEVER reload the page. A reload wipes the in-memory lock + cartToggleState,
  //     after which reconcileOnLoad / bespoke-repair re-fire against a cart the
  //     3rd-party bundler app is still rewriting → mixed-term confusion / double
  //     writes. We refresh the cart sections IN PLACE instead.
  //  2. ONE write action at a time. cartWriteInFlight guards re-entrancy so an
  //     observer tick or a stray click can't interleave a second write.
  var cartWriteInFlight = false;

  // Re-render the cart line items + summary in place (no reload), mirroring the
  // theme's OWN native qty-stepper refresh exactly. Two things the theme does
  // that we must replicate (both verified on this store):
  //   1. Fetch from the `/cart` PATH, not `/`. The cart-summary section only
  //      renders its totals + Checkout when rendered in the /cart template
  //      context; from `/` it returns a hollow shell (this blanked the summary).
  //   2. Request the TEMPLATE-SCOPED section ids (e.g.
  //      `shopify-section-template--XXXX__cart-summary` without the
  //      `shopify-section-` prefix), read at runtime from the live wrappers — NOT
  //      a hardcoded id, and NOT the bare `main-cart-summary` (that resolves to an
  //      empty shell). Then swap each live inner element's innerHTML, unwrapping
  //      the returned section wrapper first.
  function liveSectionName(innerId) {
    var inner = document.getElementById(innerId);
    var section = inner && inner.closest(".shopify-section");
    if (!section || !section.id) return null;
    return section.id.replace(/^shopify-section-/, "");
  }
  function refreshCartInPlace() {
    var targets = [
      { inner: "main-cart-items", section: liveSectionName("main-cart-items") },
      {
        inner: "main-cart-summary",
        section: liveSectionName("main-cart-summary"),
      },
    ].filter(function (t) {
      return !!t.section && !!document.getElementById(t.inner);
    });
    if (!targets.length) {
      // Can't resolve the sections — fall back to reload (cart still correct).
      window.location.reload();
      return Promise.resolve();
    }
    var names = targets
      .map(function (t) {
        return t.section;
      })
      .join(",");
    return fetch(shopRoot() + "/cart?sections=" + encodeURIComponent(names), {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (r) {
        return r.ok
          ? r.json()
          : Promise.reject(new Error("sections " + r.status));
      })
      .then(function (sections) {
        var swapped = 0;
        targets.forEach(function (t) {
          var live = document.getElementById(t.inner);
          var html = sections[t.section];
          if (!live || !html) return;
          // The returned HTML is the full section wrapper; take the inner
          // element's innerHTML so we don't double-nest a section wrapper.
          var doc = new DOMParser().parseFromString(html, "text/html");
          var fresh = doc.getElementById(t.inner);
          if (fresh) {
            live.innerHTML = fresh.innerHTML;
            swapped++;
          }
        });
        if (!swapped) throw new Error("no cart sections swapped");
      })
      .catch(function (e) {
        // Last resort only: a reload still leaves the cart correct (Shopify
        // serialises the writes); we just lose the no-flash UX.
        notify("refreshCartInPlace failed — reloading", e);
        window.location.reload();
      });
  }

  // Collect a {lineKey: 0} map for every insurance line in the cart.
  function sweepInsuranceUpdates(cart) {
    var updates = {};
    (cart.items || []).forEach(function (it) {
      if (isInsuranceLine(it)) updates[it.key] = 0;
    });
    return updates;
  }

  // After a sweep+re-add at a new term, each insurance line carries the new
  // lx_{variant}_{code} link but its product line still carries the old-code
  // link. Re-stamp every insured product's _loxaLinkId to match its insurance
  // line, so the product↔insurance pair joins correctly (reconcile stays
  // strict). Pairs by _insuredProductId (variant). Updates run sequentially
  // (Shopify serialises cart writes). No-op when every product already matches.
  function restampProductLinks() {
    return readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        var productByVariant = {};
        cart.items.forEach(function (it) {
          if (!isInsuranceLine(it)) productByVariant[String(it.variant_id)] = it;
        });
        var thunks = [];
        cart.items.forEach(function (ins) {
          if (!isInsuranceLine(ins)) return;
          var p = ins.properties || {};
          var link = p._loxaLinkId;
          var insuredVariant = p._insuredProductId;
          if (!link || !insuredVariant) return;
          var product = productByVariant[String(insuredVariant)];
          if (!product) return;
          if (product.properties && product.properties._loxaLinkId === link) {
            return; // already matches — nothing to do
          }
          var merged = {};
          var existing = product.properties || {};
          Object.keys(existing).forEach(function (k) {
            merged[k] = existing[k];
          });
          merged._loxaLinkId = link;
          var key = product.key;
          thunks.push(function () {
            return changeLinePropertiesRaw(key, merged);
          });
        });
        if (!thunks.length) return;
        return runSequentially(thunks);
      })
      .catch(function (e) {
        notify("restampProductLinks failed", e);
      });
  }

  // Build the insurance lines to add for every uninsured insurable entry in `q`
  // at `term`. Resolves to an array of {id, quantity, properties}.
  function buildInsuranceLines(q, term) {
    return Promise.all(
      q.uninsuredInsurable.map(function (entry) {
        var plan = pickInsuranceForTerm(entry.quote, term);
        if (!plan || premiumOf(plan) <= 0) return Promise.resolve(null);
        return resolveInsuranceVariantId(plan).then(function (insVid) {
          if (!insVid) return null;
          var productVariantId = String(entry.item.variant_id);
          var linkId = linkIdFor(productVariantId, plan.code);
          return {
            id: insVid,
            quantity: entry.item.quantity || 1,
            properties: buildInsuranceProps(
              plan,
              entry.quote,
              insVid,
              productVariantId,
              linkId,
            ),
          };
        });
      }),
    ).then(function (lines) {
      return lines.filter(Boolean);
    });
  }

  // Is the basket ALREADY fully normalised to `term`? (every insurable item
  // insured, and no insurance line at a different term.) Then applying is a
  // no-op — avoids needless churn (the "only normalise when terms differ" rule).
  function basketAlreadyAtTerm(q, term) {
    if (!q || q.uninsuredInsurable.length > 0) return false; // gaps remain
    var terms = q.insuredTerms || [];
    if (!terms.length) return false;
    return terms.length === 1 && String(terms[0]) === String(term);
  }

  // THE single apply entry point (toggle ON + sidebar Add). Normalises the WHOLE
  // basket to `term`: if already fully at that term, no-op; otherwise sweep ALL
  // insurance (any term/origin) and re-add `term` across every insurable item —
  // so a 3yr line + a 5yr request ends up all-5yr, never mixed.
  function setBasketTerm(term) {
    var t = term || DEFAULT_TERM;
    selectedTerm = t;
    if (cartWriteInFlight) return Promise.resolve();
    if (basketAlreadyAtTerm(cartQuote, t)) {
      cartToggleState = "ready_on";
      renderCtaState();
      return Promise.resolve();
    }
    cartWriteInFlight = true;
    cartToggleState = "applying";
    renderCtaState();
    return readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        var updates = sweepInsuranceUpdates(cart);
        if (!Object.keys(updates).length) return;
        return updateLineQuantitiesRaw(updates); // remove all existing insurance
      })
      .then(function () {
        return quoteBasket(); // everything is now uninsured
      })
      .then(function (fresh) {
        cartQuote = fresh;
        return buildInsuranceLines(fresh, t);
      })
      .then(function (items) {
        if (!items.length) return;
        return addLinesRaw(items); // single multi-item POST at the chosen term
      })
      .then(function () {
        // The link id encodes the insurance code (lx_{variant}_{code}); the
        // sweep+re-add rebuilt insurance lines at the NEW code, but the product
        // lines still carry the OLD-code link. Re-stamp each insured product's
        // _loxaLinkId to its insurance line's new link so the pair matches —
        // otherwise reconcileOnLoad sees an orphan and drops the insurance.
        return restampProductLinks();
      })
      .then(function () {
        return refreshCartInPlace();
      })
      .then(function () {
        return refreshCartToggle(); // re-derive state from the now-current cart
      })
      .catch(function (e) {
        notify("setBasketTerm failed", e);
        cartToggleState = "error_off";
        renderCtaState();
      })
      .then(function () {
        cartWriteInFlight = false;
      });
  }

  // Toggle ON quick-applies at the currently-selected term.
  function applyBasket(term) {
    return setBasketTerm(term || selectedTerm);
  }

  // ── Remove (toggle OFF) — clean sweep of ALL insurance lines ─────────────────
  function removeAllInsurance() {
    if (cartWriteInFlight) return Promise.resolve();
    cartWriteInFlight = true;
    cartToggleState = "removing";
    renderCtaState();
    return readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        var updates = sweepInsuranceUpdates(cart);
        if (!Object.keys(updates).length) return;
        return updateLineQuantitiesRaw(updates);
      })
      .then(function () {
        return refreshCartInPlace();
      })
      .then(function () {
        return refreshCartToggle();
      })
      .catch(function (e) {
        notify("removeAllInsurance failed", e);
        cartToggleState = "error_on";
        renderCtaState();
      })
      .then(function () {
        cartWriteInFlight = false;
      });
  }

  // ── See Details sidebar — basket protection totals + furniture terms ──────────
  // Every DreamCloud product is Furniture, so one disclaimer covers the basket.
  // We prefer the core block's Loxa.getTerms("furniture") when present, but
  // window.Loxa is NOT loaded on /cart, so fall back to a self-contained
  // furniture disclaimer with the static IPID + Policy Wording links.
  var FURNITURE_IPID = "https://www.loxacover.com/furniture-insurance-ipid-latest";
  var FURNITURE_PW =
    "https://www.loxacover.com/furniture-insurance-policy-wording-latest";
  function cartSidebarTerms() {
    var Lc = window.Loxa;
    if (Lc && typeof Lc.getTerms === "function") {
      var t = Lc.getTerms("furniture");
      if (t) return t;
    }
    return (
      "Provided by Loxa. By adding this insurance to your basket you confirm " +
      "that you are a UK resident over 18, that it is your responsibility to " +
      "read and understand the " +
      '<a href="' +
      FURNITURE_IPID +
      '" target="_blank" rel="noopener">IPID</a> and ' +
      '<a href="' +
      FURNITURE_PW +
      '" target="_blank" rel="noopener">Policy Wording</a>, ' +
      "and that this product meets your needs."
    );
  }
  // "What to expect" list — generic furniture AD&breakdown wording, mirrors the
  // PDP sidebar so the cart sidebar reads identically.
  var CART_TICK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5l10 -10"></path></svg>';
  var CART_MINUS_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path></svg>';
  function cartWhatToExpect() {
    return (
      '<div class="loxa-sidebar-extra"><div class="loxa-sidebar-terms">' +
      "<h4>What to expect</h4><ul>" +
      "<li>" + CART_TICK_SVG + "<span>Covers sudden accidental damage where treatment, repair or replacement is needed</span></li>" +
      "<li>" + CART_TICK_SVG + "<span>Includes stains, spills, rips, tears, burns, dents and chips</span></li>" +
      "<li>" + CART_TICK_SVG + "<span>Includes sudden accidental damage caused by pets</span></li>" +
      "<li>" + CART_TICK_SVG + "<span>For approved claims, we will arrange the right fix</span></li>" +
      "<li>" + CART_MINUS_SVG + "<span>Does not cover wear and tear, misuse, repeated damage over time, minor marks, or issues already covered by your manufacturer warranty.</span></li>" +
      "<li>" + CART_MINUS_SVG + '<span>For full details of what\'s covered and excluded, see the <a href="' + FURNITURE_IPID + '" target="_blank" rel="noopener">IPID</a> and <a href="' + FURNITURE_PW + '" target="_blank" rel="noopener">Policy Wording</a>.</span></li>' +
      "</ul>" +
      '<div class="loxa-sidebar-legal"><p>This insurance is only available to residents of the UK. Loxa is an insurance intermediary authorised and regulated by the Financial Conduct Authority (FCA no. 1018666). Your cover is underwritten by Helvetia Global Solutions Ltd (FCA no. 454140).</p></div>' +
      "</div></div>"
    );
  }

  // Build a term option card (matches the PDP sidebar's .loxa-sidebar-option so
  // it inherits the block's styling). `term` is "5" or "3".
  function cartTermOption(term, pricePence, selected) {
    return (
      '<label class="loxa-sidebar-option' +
      (selected ? " loxa-selected" : "") +
      '" data-loxa-term="' +
      term +
      '">' +
      '<input type="radio" name="loxa-dc-cart-term" value="' +
      term +
      '"' +
      (selected ? " checked" : "") +
      ">" +
      '<div class="loxa-sidebar-option-body">' +
      '<div class="loxa-sidebar-option-main">' +
      '<span class="loxa-sidebar-option-circle">' +
      (selected ? CART_TICK_SVG : "") +
      "</span>" +
      '<span class="loxa-sidebar-option-title">' +
      termLabel(term) +
      " protection</span>" +
      "</div>" +
      '<span class="loxa-sidebar-option-price">' +
      poundsFromPence(pricePence) +
      "</span>" +
      "</div>" +
      "</label>"
    );
  }

  function buildCartSidebarHtml(q) {
    var fiveSel = selectedTerm !== "3";
    return (
      '<div class="loxa-sidebar-content-root">' +
      '<div class="loxa-sidebar-intro">' +
      "<h3>Add Protection, Enjoy Peace of Mind</h3>" +
      '<p class="loxa-sidebar-powered-by">Powered by Loxa</p>' +
      '<p class="loxa-sidebar-subheading">Protect your furniture against sudden accidents and unexpected faults, with repair or replacement where needed.</p>' +
      "</div>" +
      cartTermOption("5", q.protectTotal5yr, fiveSel) +
      cartTermOption("3", q.protectTotal3yr, !fiveSel) +
      '<div class="loxa-sidebar-actions">' +
      '<button type="button" class="loxa-sidebar-cta loxa-dc-cart-apply">' +
      (q.uninsuredInsurable.length === 0 ? "Update protection" : "Add Protection") +
      "</button>" +
      "</div>" +
      cartWhatToExpect() +
      "</div>"
    );
  }

  function closeCartSidebar() {
    // Close the panel ourselves. (We can't defer to LoxaShopify.closeSidebar
    // alone because we forced the overlay's display:block above, which the
    // block's close won't reset — so we own the full teardown here.)
    if (
      window.LoxaShopify &&
      typeof window.LoxaShopify.closeSidebar === "function"
    ) {
      try {
        window.LoxaShopify.closeSidebar();
      } catch (e) {}
    }
    var sidebar = document.getElementById("loxa-shopify-sidebar");
    var overlay = document.getElementById("loxa-shopify-sidebar-overlay");
    if (sidebar) {
      sidebar.classList.remove("loxa-sidebar-open");
      sidebar.style.right = "-100%";
      sidebar.style.left = "-100%";
    }
    if (overlay) {
      overlay.classList.remove("loxa-open");
      overlay.style.display = "none";
    }
  }

  var cartSidebarCloseWired = false;
  function openCartSidebar() {
    var sidebar = document.getElementById("loxa-shopify-sidebar");
    var content = document.getElementById("loxa-shopify-sidebar-content");
    if (!sidebar || !content) {
      notify("openCartSidebar: sidebar shell missing");
      return;
    }
    content.innerHTML = buildCartSidebarHtml(cartQuote || emptyBasketQuote());
    sidebar.setAttribute(
      "data-loxa-theme",
      (window.LoxaShopify && window.LoxaShopify.themeMode) || "light",
    );
    // Wire the term option cards (re-wired each open — content is rebuilt).
    // Selecting a card sets the term, repaints the cards' selected state, and
    // updates the toggle wording. It does NOT apply — Add Protection does.
    var options = content.querySelectorAll(".loxa-sidebar-option[data-loxa-term]");
    options.forEach(function (opt) {
      opt.addEventListener("click", function () {
        selectedTerm = opt.getAttribute("data-loxa-term") || DEFAULT_TERM;
        options.forEach(function (o) {
          var on = o === opt;
          o.classList.toggle("loxa-selected", on);
          var circle = o.querySelector(".loxa-sidebar-option-circle");
          if (circle) circle.innerHTML = on ? CART_TICK_SVG : "";
          var radio = o.querySelector('input[type="radio"]');
          if (radio) radio.checked = on;
        });
        renderCtaState(); // toggle wording follows the chosen term
      });
    });
    // Wire "Add Protection": apply the selected term to the whole basket
    // (clean-sweep + re-add if switching term), then close.
    var applyBtn = content.querySelector(".loxa-dc-cart-apply");
    if (applyBtn) {
      applyBtn.addEventListener("click", function (e) {
        e.preventDefault();
        closeCartSidebar();
        setBasketTerm(selectedTerm);
      });
    }
    // Wire our OWN close handlers once. The block only binds X/overlay/Escape
    // inside its openSidebar (guarded by _sidebarInitialized), which never runs
    // on /cart — so without this the sidebar can't be closed here.
    if (!cartSidebarCloseWired) {
      cartSidebarCloseWired = true;
      var closeBtn = sidebar.querySelector(".loxa-sidebar-close");
      if (closeBtn) {
        closeBtn.addEventListener("click", function (e) {
          e.preventDefault();
          closeCartSidebar();
        });
      }
      var overlayEl = document.getElementById("loxa-shopify-sidebar-overlay");
      if (overlayEl) {
        overlayEl.addEventListener("click", function () {
          closeCartSidebar();
        });
      }
      document.addEventListener("keydown", function (e) {
        if (
          e.key === "Escape" &&
          sidebar.classList.contains("loxa-sidebar-open")
        ) {
          closeCartSidebar();
        }
      });
    }
    sidebar.classList.add("loxa-sidebar-open");
    var overlay = document.getElementById("loxa-shopify-sidebar-overlay");
    if (overlay) {
      overlay.classList.add("loxa-open");
      // The block's overlay is display:none until opened on a PDP; force it
      // visible + hittable here so the overlay-click close works on /cart.
      overlay.style.display = "block";
    }
    var side =
      (window.LoxaShopify &&
        typeof window.LoxaShopify.getSidebarSide === "function" &&
        window.LoxaShopify.getSidebarSide()) ||
      "right";
    // closeCartSidebar parks the panel with BOTH left:-100% and right:-100%.
    // Clear the opposite side here, else the stale offset wins over our "0"
    // (explicit width → left beats right) and the panel opens off-screen.
    var opposite = side === "right" ? "left" : "right";
    sidebar.style[opposite] = "auto";
    sidebar.style[side] = "0";
  }

  // ── Reconcile-on-load (mechanism-independent safety net) ──────────────────
  // Sync each insurance line's qty to its paired product's qty, and drop
  // orphan insurance lines whose product was removed. Keyed on _loxaLinkId,
  // falling back to _insuredProductId if a 3rd-party app dropped the link.
  function reconcileOnLoad() {
    if (window.__loxaDcReconciled) return;
    window.__loxaDcReconciled = true;
    readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        var products = {}; // join key -> product line
        cart.items.forEach(function (it) {
          if (isInsuranceLine(it)) return;
          var key =
            (it.properties && it.properties._loxaLinkId) ||
            String(it.variant_id);
          products[key] = it;
        });

        var updates = {};
        cart.items.forEach(function (it) {
          if (!isInsuranceLine(it)) return;
          var p = it.properties || {};
          var key = p._loxaLinkId || p._insuredProductId;
          var product = key ? products[key] : null;
          // also try product keyed by variant id (link dropped by Bespoke)
          if (!product && p._insuredProductId)
            product = products[p._insuredProductId];
          if (!product) {
            // Orphan: product removed → drop insurance line.
            updates[it.key] = 0;
          } else if (it.quantity !== product.quantity) {
            // Qty drift: match the product.
            updates[it.key] = product.quantity;
          }
        });

        if (Object.keys(updates).length === 0) return;
        return updateLineQuantitiesRaw(updates).then(function () {
          // A native reload already happened to land here; re-render the cart
          // sections so the corrected qty/removal is visible without a 2nd nav.
          refreshCartSections();
        });
      })
      .catch(function (e) {
        notify("reconcileOnLoad failed", e);
      });
  }

  // ── Atomic qty/remove overrides (Dawn AJAX /cart/change.js) ───────────────
  // When the customer changes a product line's qty (or removes it) via Dawn's
  // AJAX controls, also update its paired insurance line in the SAME
  // /cart/update.js, so insurance qty tracks the product with no reload flash.
  // Implemented by overriding core's intercept hooks; guard header prevents
  // re-entry. Reconcile-on-load remains the safety net for any path that slips.
  function installAtomicOverrides() {
    try {
      var L = window.LoxaShopify;
      if (!L || L._loxaDcAtomicInstalled) return;
      L._loxaDcAtomicInstalled = true;

      var origChange =
        typeof L._handleCartChangeIntercept === "function"
          ? L._handleCartChangeIntercept.bind(L)
          : null;
      var origUpdate =
        typeof L._handleCartUpdateIntercept === "function"
          ? L._handleCartUpdateIntercept.bind(L)
          : null;

      // We wrap window.fetch ourselves (DreamCloud's qty controls call
      // /cart/change.js directly; we don't rely on core's own wrapper firing).
      if (window.__loxaDcFetchWrapped) return;
      window.__loxaDcFetchWrapped = true;
      var origFetch = window.fetch.bind(window);

      window.fetch = function (input, init) {
        try {
          var url =
            typeof input === "string" ? input : (input && input.url) || "";
          var method =
            (init && init.method) ||
            (typeof input !== "string" && input && input.method) ||
            "GET";
          // Only act on our targets, only on POSTs we didn't originate.
          var isChange = /\/cart\/change(\.js)?(\?|$)/.test(url);
          if (
            isChange &&
            String(method).toUpperCase() === "POST" &&
            !(init && init.headers && headerHas(init.headers, ATOMIC_HEADER)) &&
            !(init && init.headers && headerHas(init.headers, RECONCILE_HEADER))
          ) {
            return atomicChange(origFetch, input, init);
          }
        } catch (e) {
          notify("fetch wrapper failed — passing through", e);
        }
        return origFetch(input, init);
      };
    } catch (e) {
      notify("installAtomicOverrides failed", e);
    }
  }

  function headerHas(headers, name) {
    try {
      if (!headers) return false;
      if (typeof headers.get === "function") return !!headers.get(name);
      var lower = name.toLowerCase();
      return Object.keys(headers).some(function (k) {
        return k.toLowerCase() === lower;
      });
    } catch (e) {
      return false;
    }
  }

  // Translate a single product qty change into one /cart/update.js touching the
  // product AND its paired insurance line. Falls through to the original change
  // on any uncertainty so we never block the customer's action.
  function atomicChange(origFetch, input, init) {
    var L = window.LoxaShopify;
    var parsed = null;
    try {
      parsed =
        L && typeof L._parseRequestBody === "function"
          ? L._parseRequestBody(init)
          : JSON.parse((init && init.body) || "{}");
    } catch (e) {
      parsed = null;
    }
    if (!parsed || (parsed.id === undefined && parsed.line === undefined)) {
      return origFetch(input, init); // not a shape we handle
    }
    var targetKeyOrLine = parsed.id !== undefined ? String(parsed.id) : null;
    var newQty =
      typeof parsed.quantity === "number"
        ? parsed.quantity
        : parseInt(parsed.quantity, 10);

    return readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return origFetch(input, init);
        // Resolve which product line is being changed.
        var product = null;
        if (targetKeyOrLine) {
          product = cart.items.find(function (it) {
            return it.key === targetKeyOrLine;
          });
        } else if (parsed.line !== undefined) {
          product = cart.items[parseInt(parsed.line, 10) - 1];
        }
        if (!product || isInsuranceLine(product)) {
          return origFetch(input, init); // changing an insurance line directly → let it be
        }
        // Find paired insurance line.
        var pkey =
          (product.properties && product.properties._loxaLinkId) ||
          String(product.variant_id);
        var insurance = cart.items.find(function (it) {
          if (!isInsuranceLine(it)) return false;
          var p = it.properties || {};
          return (
            (p._loxaLinkId && p._loxaLinkId === pkey) ||
            (p._insuredProductId &&
              p._insuredProductId === String(product.variant_id))
          );
        });
        if (!insurance) return origFetch(input, init); // nothing to mirror

        // One atomic update for product + insurance.
        var updates = {};
        updates[product.key] = isNaN(newQty) ? product.quantity : newQty;
        updates[insurance.key] = isNaN(newQty) ? insurance.quantity : newQty;
        var headers = { "Content-Type": "application/json" };
        headers[ATOMIC_HEADER] = "1";
        return origFetch(shopRoot() + "/cart/update.js", {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ updates: updates }),
        }).then(function (resp) {
          refreshCartSections();
          return resp;
        });
      })
      .catch(function (e) {
        notify("atomicChange failed — passing through", e);
        return origFetch(input, init);
      });
  }

  // ── Bespoke Shipping repair ───────────────────────────────────────────────
  // Bespoke recalculates the cart and stamps _bespokeid/_bskey on every line;
  // it may drop our _loxaLinkId. After a Bespoke write settles, re-read the
  // cart and re-stamp any insurance line missing its link (remove + re-add,
  // since Shopify line properties are immutable).
  function installBespokeRepair() {
    if (window.__loxaDcBespokeRepair) return;
    window.__loxaDcBespokeRepair = true;
    try {
      var origOpen = XMLHttpRequest.prototype.open;
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          this.__loxaDcUrl = String(url || "");
          this.__loxaDcMethod = String(method || "").toUpperCase();
        } catch (e) {}
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        try {
          if (
            this.__loxaDcMethod === "POST" &&
            /\/cart\/(change|update)(\.js)?/.test(this.__loxaDcUrl || "")
          ) {
            var xhr = this;
            xhr.addEventListener("load", function () {
              if (xhr.status >= 200 && xhr.status < 300)
                scheduleBespokeRepair();
            });
          }
        } catch (e) {}
        return origSend.apply(this, arguments);
      };
    } catch (e) {
      notify("installBespokeRepair wrap failed", e);
    }
  }

  var _bespokeScheduled = false;
  function scheduleBespokeRepair() {
    if (_bespokeScheduled) return;
    _bespokeScheduled = true;
    setTimeout(function () {
      _bespokeScheduled = false;
      runBespokeRepair();
    }, 300);
  }

  function runBespokeRepair() {
    // Don't interfere while the cart toggle is mid-write (sweep/add) — it would
    // read a half-written cart and could reload mid-sequence.
    if (cartWriteInFlight) return;
    readCart()
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        // Build product lookup by variant id for re-derivable link repair.
        var thunks = [];
        cart.items.forEach(function (ins) {
          if (!isInsuranceLine(ins)) return;
          var p = ins.properties || {};
          if (p._loxaLinkId) return; // link intact, nothing to repair
          if (!p._insuredProductId || !p._loxaInsuranceCodeSep) return;
          var linkId = linkIdFor(p._insuredProductId, p._loxaInsuranceCodeSep);
          var newProps = {};
          Object.keys(p).forEach(function (k) {
            newProps[k] = p[k];
          });
          newProps._loxaLinkId = linkId;
          // Remove + re-add (props immutable). Delete old, add fresh.
          thunks.push(function () {
            var del = {};
            del[ins.key] = 0;
            return updateLineQuantitiesRaw(del);
          });
          thunks.push(function () {
            return addLineRaw(
              p._loxaInsuranceProductId || ins.variant_id,
              ins.quantity,
              newProps,
            );
          });
        });
        if (!thunks.length) return;
        return runSequentially(thunks).then(refreshCartSections);
      })
      .catch(function (e) {
        notify("runBespokeRepair failed", e);
      });
  }

  // ── Cart section re-render (Dawn) ─────────────────────────────────────────
  function refreshCartSections() {
    try {
      // Simple, reliable on a page-reload store: re-fetch the cart sections and
      // swap, or reload as a fallback. Dawn ids vary by theme, so reload is the
      // safe default after our own atomic write (the customer already sees the
      // product change; this brings the insurance line in line).
      window.location.reload();
    } catch (e) {
      notify("refreshCartSections failed", e);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BOOT (self-detect page type)
  // ═══════════════════════════════════════════════════════════════════════════
  function isCartPage() {
    return /(^|\/)cart(\/|$|\?)/.test(
      window.location.pathname + window.location.search,
    );
  }

  function boot() {
    try {
      if (isCartPage()) {
        waitFor(devReady, initCart, function () {
          notify("LoxaShopifyDev not ready on /cart after 8s");
        });
        return;
      }
      if (/\/products\/[^/?#]+/.test(window.location.pathname)) {
        var t = pdpTemplate();
        if (t === "unknown") {
          log("PDP excluded (template=" + t + ")");
          return;
        }
        // Only mattress + bedframe init. Bundle PDPs are a no-op (pdpTemplate
        // returns "unknown" for them — the 3rd-party bundle app owns the add).
        waitFor(devReady, initPdp, function () {
          notify("LoxaShopifyDev not ready on PDP after 8s");
        });
      }
    } catch (e) {
      notify("boot failed", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
